import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { api, brl, formatDate } from "../lib/api";

type Tab =
  | "events"
  | "users"
  | "activations"
  | "subscriptions"
  | "payments"
  | "refunds"
  | "campaigns"
  | "catalog"
  | "clients"
  | "outbound"
  | "radar"
  | "fiscal"
  | "connect"
  | "coupons"
  | "settings";

const TAB_LABELS: Record<Tab, string> = {
  events: "Webhooks",
  users: "Usuários",
  activations: "Ativações",
  subscriptions: "Assinaturas",
  payments: "Pagamentos",
  refunds: "Reembolsos",
  campaigns: "Campanhas",
  catalog: "Catálogo",
  clients: "Clientes Externos",
  outbound: "Webhooks Saída",
  radar: "Radar",
  fiscal: "Notas Fiscais",
  connect: "Connect / Afiliados",
  coupons: "Cupons",
  settings: "Configurações",
};

// Navegação lateral (estilo admin tradicional): os 15 itens agrupados por
// seção na sidebar, em vez de uma barra de abas + dropdown "Mais".
const NAV_GROUPS: { title: string; tabs: Tab[] }[] = [
  { title: "Visão geral", tabs: ["events", "activations", "outbound"] },
  { title: "Vendas", tabs: ["payments", "subscriptions", "refunds"] },
  { title: "Catálogo", tabs: ["catalog", "campaigns", "coupons"] },
  { title: "Pessoas", tabs: ["users", "clients"] },
  { title: "Integrações", tabs: ["radar", "fiscal", "connect"] },
  { title: "Sistema", tabs: ["settings"] },
];

type Stats = {
  users: number;
  activeFeatures: number;
  activeSubscriptions: number;
  paymentIntents: number;
  refunds: number;
};

type AdminUser = {
  id: number;
  email: string;
  name: string | null;
  stripeCustomerId: string | null;
  createdAt: string;
  activationsCount: number;
  activeFeaturesCount: number;
  paymentsCount: number;
  subscriptionsCount: number;
};

type AdminActivation = {
  id: number;
  userId: number | null;
  userEmail: string | null;
  userName: string | null;
  featureKey: string;
  status: string;
  expiresAt: string | null;
  stripeSubscriptionId: string | null;
  stripePaymentIntentId: string | null;
  activatedAt: string;
  deactivatedAt: string | null;
  deactivationReason: string | null;
};

type AdminSubscriptionItem = {
  stripeItemId: string;
  stripePriceId: string;
  featureKey: string;
  quantity: number;
  status: string;
  addedAt: string;
  removedAt: string | null;
};

type AdminSubscription = {
  id: number;
  userId: number | null;
  userEmail: string | null;
  stripeSubscriptionId: string;
  status: string;
  currentPeriodEnd: string | null;
  canceledAt: string | null;
  cancelReason: string | null;
  createdAt: string;
  campaignId: string | null;
  source: string | null;
  brand: string | null;
  promoCode: string | null;
  items: AdminSubscriptionItem[];
};

type AdminPayment = {
  id: number;
  userId: number | null;
  userEmail: string | null;
  stripePaymentIntentId: string;
  amount: number;
  currency: string | null;
  status: string;
  productKey: string | null;
  createdAt: string;
  campaignId: string | null;
  source: string | null;
  brand: string | null;
  promoCode: string | null;
};

const PAYMENT_SOURCES = ["storefront", "campaign", "gift", "renewal", "manual"];

type AdminRefund = {
  id: number;
  userId: number | null;
  userEmail: string | null;
  stripeRefundId: string;
  stripePaymentIntentId: string | null;
  amount: number;
  reason: string | null;
  status: string;
  createdAt: string;
  productKey: string | null;
  campaignId: string | null;
  source: string | null;
  brand: string | null;
  promoCode: string | null;
};

type WebhookRow = {
  id: number;
  eventId: string;
  eventType: string;
  status: string;
  processingTimeMs: number | null;
  createdAt: string;
  objectType: string | null;
  objectId: string | null;
  customerId: string | null;
  amountCents: number | null;
  currency: string | null;
  email: string | null;
};

type RadarRow = {
  id: number;
  stripeChargeId: string;
  stripePaymentIntentId: string | null;
  userId: number | null;
  userEmail: string | null;
  amount: number | null;
  currency: string | null;
  riskLevel: string | null;
  riskScore: number | null;
  outcomeType: string | null;
  networkStatus: string | null;
  sellerMessage: string | null;
  ruleId: string | null;
  reviewId: string | null;
  reviewStatus: string | null;
  reviewOpenedReason: string | null;
  reviewClosedReason: string | null;
  createdAt: string;
};

type FiscalRow = {
  id: number;
  provider: string;
  userId: number | null;
  userEmail: string | null;
  stripePaymentIntentId: string | null;
  stripeChargeId: string | null;
  amount: number;
  currency: string;
  status: string;
  providerInvoiceId: string | null;
  pdfUrl: string | null;
  errorMessage: string | null;
  issuedAt: string | null;
  canceledAt: string | null;
  createdAt: string;
};

type ConnectAccount = {
  id: number;
  stripeAccountId: string;
  accountType: string;
  affiliateCode: string | null;
  name: string | null;
  email: string | null;
  commissionPct: number;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  status: string;
  createdAt: string;
};

type ConnectTransfer = {
  id: string;
  amount: number;
  currency: string;
  amountReversed: number;
  reversed: boolean;
  destination: string | null;
  affiliateCode: string | null;
  affiliateName: string | null;
  saleAmount: number | null;
  sourceCharge: string | null;
  created: string;
};

type CouponRow = {
  promotionCodeId: string;
  code: string;
  active: boolean;
  timesRedeemed: number;
  maxRedemptions: number | null;
  expiresAt: number | null;
  percentOff: number | null;
  amountOff: number | null;
  currency: string | null;
  duration: string | null;
  valid: boolean;
};

function Pill({ status }: { status: string }) {
  return <span className={`pill pill-${status}`}>{status}</span>;
}

export function Admin() {
  const [tab, setTab] = useState<Tab>("events");
  const [stats, setStats] = useState<Stats | null>(null);
  // Drawer da sidebar no mobile (no desktop a sidebar fica sempre visível).
  const [navOpen, setNavOpen] = useState(false);

  const selectTab = (t: Tab) => {
    setTab(t);
    setNavOpen(false);
  };

  const refreshStats = useCallback(() => {
    api<Stats>("/api/admin/stats").then((r) => r.ok && setStats(r.body));
  }, []);

  useEffect(() => {
    refreshStats();
    const t = setInterval(refreshStats, 4000);
    return () => clearInterval(t);
  }, [refreshStats]);

  return (
    <div className={navOpen ? "admin-shell nav-open" : "admin-shell"}>
      <aside className="admin-sidebar">
        <div className="admin-sidebar-head">
          <span>Navegação</span>
          <button
            className="admin-nav-close"
            onClick={() => setNavOpen(false)}
            aria-label="Fechar menu"
          >
            &times;
          </button>
        </div>
        <nav className="admin-nav-list">
          {NAV_GROUPS.map((g) => (
            <div className="admin-nav-group" key={g.title}>
              <div className="admin-nav-group-title">{g.title}</div>
              {g.tabs.map((t) => (
                <button
                  key={t}
                  onClick={() => selectTab(t)}
                  className={tab === t ? "admin-nav-item active" : "admin-nav-item"}
                >
                  {TAB_LABELS[t]}
                </button>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      {navOpen && (
        <div
          className="admin-nav-backdrop"
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="admin-main">
        <header className="page-header admin-topbar">
          <button
            className="admin-nav-toggle"
            onClick={() => setNavOpen(true)}
            aria-label="Abrir menu"
          >
            <span />
            <span />
            <span />
          </button>
          <div>
            <h1>{TAB_LABELS[tab]}</h1>
          </div>
        </header>

        {/* KPIs globais so' na tela inicial (Webhooks = visao geral); poluiam
            todas as abas antes. */}
        {tab === "events" && stats && (
          <div className="stats-grid">
            <Stat label="Usuários" value={stats.users} />
            <Stat label="Features ativas" value={stats.activeFeatures} />
            <Stat label="Assinaturas ativas" value={stats.activeSubscriptions} />
            <Stat label="Pagamentos" value={stats.paymentIntents} />
            <Stat label="Reembolsos" value={stats.refunds} />
          </div>
        )}

        <div className="tab-content">
          {tab === "events" && <EventsTab />}
          {tab === "users" && <UsersTab />}
          {tab === "activations" && <ActivationsTab />}
          {tab === "subscriptions" && <SubscriptionsTab />}
          {tab === "payments" && <PaymentsTab />}
          {tab === "refunds" && <RefundsTab />}
          {tab === "campaigns" && <CampaignsTab />}
          {tab === "catalog" && <CatalogTab />}
          {tab === "clients" && <ServiceClientsTab />}
          {tab === "outbound" && <OutboundTab />}
          {tab === "radar" && <RadarTab />}
          {tab === "fiscal" && <FiscalTab />}
          {tab === "connect" && <ConnectTab />}
          {tab === "coupons" && <CouponsTab />}
          {tab === "settings" && <SettingsTab />}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="stat-card">
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

// Igual ao Stat, mas pra valores ja formatados (ex: moeda).
function KpiCard({ label, text }: { label: string; text: string }) {
  return (
    <div className="stat-card">
      <div className="stat-value">{text}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

const EVENT_PAGE = 30;
const EVENT_STATUSES = ["received", "processing", "processed", "failed"];

function EventsTab() {
  const [events, setEvents] = useState<WebhookRow[]>([]);
  const [count, setCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [objectTypes, setObjectTypes] = useState<string[]>([]);
  const [filters, setFilters] = useState({
    objectType: "",
    status: "",
    eventType: "",
    search: "",
  });
  // Inputs de texto livre aplicados no submit (Enter/Buscar), pra não refazer
  // a query a cada tecla.
  const [searchInput, setSearchInput] = useState("");
  const [eventTypeInput, setEventTypeInput] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [detail, setDetail] = useState<unknown>(null);

  const refresh = useCallback(() => {
    const p = new URLSearchParams({
      limit: String(EVENT_PAGE),
      offset: String(offset),
    });
    if (filters.objectType) p.set("object_type", filters.objectType);
    if (filters.status) p.set("status", filters.status);
    if (filters.eventType) p.set("event_type", filters.eventType);
    if (filters.search) p.set("search", filters.search);
    api<{ data: WebhookRow[]; count: number }>(
      `/api/admin/events?${p.toString()}`
    ).then((r) => {
      if (r.ok) {
        setEvents(r.body.data);
        setCount(r.body.count);
      }
    });
  }, [filters, offset]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Popula o dropdown de object_type via /distinct (uma vez).
  useEffect(() => {
    api<{ data: { value: string; count: number }[] }>(
      "/api/admin/events/distinct?field=object_type"
    ).then((r) => {
      if (r.ok) setObjectTypes(r.body.data.map((d) => d.value));
    });
  }, []);

  // Mudar filtro sempre volta pra primeira página.
  const setFilter = (patch: Partial<typeof filters>) => {
    setOffset(0);
    setFilters((f) => ({ ...f, ...patch }));
  };
  const applyText = (e: FormEvent) => {
    e.preventDefault();
    setFilter({ search: searchInput.trim(), eventType: eventTypeInput.trim() });
  };
  const clearAll = () => {
    setSearchInput("");
    setEventTypeInput("");
    setOffset(0);
    setFilters({ objectType: "", status: "", eventType: "", search: "" });
  };

  const toggle = async (eventId: string) => {
    if (expanded === eventId) {
      setExpanded(null);
      setDetail(null);
      return;
    }
    setExpanded(eventId);
    setDetail(null);
    const r = await api(`/api/admin/events/${eventId}`);
    setDetail(r.body);
  };

  const from = count === 0 ? 0 : offset + 1;
  const to = Math.min(offset + EVENT_PAGE, count);

  return (
    <div>
      <form
        onSubmit={applyText}
        style={{
          display: "flex",
          gap: "0.5rem",
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: "0.75rem",
        }}
      >
        <input
          placeholder="Buscar no payload..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <input
          placeholder="event_type"
          value={eventTypeInput}
          onChange={(e) => setEventTypeInput(e.target.value)}
        />
        <select
          value={filters.objectType}
          onChange={(e) => setFilter({ objectType: e.target.value })}
        >
          <option value="">objeto: todos</option>
          {objectTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilter({ status: e.target.value })}
        >
          <option value="">status: todos</option>
          {EVENT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button type="submit" className="primary-btn">
          Buscar
        </button>
        <button type="button" className="ghost-btn" onClick={clearAll}>
          Limpar
        </button>
      </form>

      <div className="muted" style={{ marginBottom: "0.5rem" }}>
        {count} eventos{count > 0 ? ` · mostrando ${from}–${to}` : ""}
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th>Horário</th>
            <th>Tipo</th>
            <th>Objeto</th>
            <th>Valor</th>
            <th>Status</th>
            <th>Latência</th>
          </tr>
        </thead>
        <tbody>
          {events.length === 0 ? (
            <tr>
              <td colSpan={6} className="empty">
                Sem eventos.
              </td>
            </tr>
          ) : (
            events.map((e) => (
              <Fragment key={e.id}>
                <tr
                  onClick={() => toggle(e.eventId)}
                  className={`clickable ${expanded === e.eventId ? "row-expanded" : ""}`}
                >
                  <td>{formatDate(e.createdAt)}</td>
                  <td>
                    <code>{e.eventType}</code>
                  </td>
                  <td>
                    {e.objectType ? (
                      <>
                        <code>{e.objectType}</code>
                        {e.objectId && (
                          <div className="muted" style={{ fontSize: "0.8em" }}>
                            {e.objectId}
                          </div>
                        )}
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    {e.amountCents != null
                      ? brl(e.amountCents, e.currency ?? "brl")
                      : "—"}
                  </td>
                  <td>
                    <Pill status={e.status} />
                  </td>
                  <td>
                    {e.processingTimeMs != null ? `${e.processingTimeMs}ms` : "—"}
                  </td>
                </tr>
                {expanded === e.eventId && (
                  <tr className="row-detail">
                    <td colSpan={6}>
                      {detail === null ? (
                        <p>Carregando payload...</p>
                      ) : (
                        <pre>{JSON.stringify(detail, null, 2)}</pre>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))
          )}
        </tbody>
      </table>

      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          alignItems: "center",
          marginTop: "0.75rem",
        }}
      >
        <button
          className="ghost-btn"
          disabled={offset === 0}
          onClick={() => setOffset(Math.max(0, offset - EVENT_PAGE))}
        >
          Anterior
        </button>
        <button
          className="ghost-btn"
          disabled={offset + EVENT_PAGE >= count}
          onClick={() => setOffset(offset + EVENT_PAGE)}
        >
          Próxima
        </button>
      </div>
    </div>
  );
}

type SettingMeta = {
  key: string;
  type: "boolean" | "enum";
  values?: string[];
  defaultValue: boolean | string;
  scopes: string[];
  category: string;
  label: string;
  description: string;
};

type SettingOverride = {
  key: string;
  scope: string;
  scopeId: string;
  value: boolean | string;
  updatedBy: string | null;
  updatedAt: string;
};

// Regras de negócio configuráveis (proração, cancelamento, repasse de afiliado,
// NF). v1: edita valores GLOBAIS. Overrides por marca/produto existem na API
// (PUT com scope=brand|product) mas ainda não têm UI dedicada.
function SettingsTab() {
  const [registry, setRegistry] = useState<SettingMeta[]>([]);
  const [overrides, setOverrides] = useState<SettingOverride[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const refresh = useCallback(() => {
    api<{ registry: SettingMeta[]; overrides: SettingOverride[] }>(
      "/api/admin/settings"
    ).then((r) => {
      if (r.ok) {
        setRegistry(r.body.registry);
        setOverrides(r.body.overrides);
      }
    });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const globalOverride = (key: string) =>
    overrides.find((o) => o.key === key && o.scope === "global");
  const effectiveValue = (m: SettingMeta): boolean | string =>
    globalOverride(m.key)?.value ?? m.defaultValue;

  const save = async (m: SettingMeta, value: boolean | string) => {
    setBusy(m.key);
    setMsg(null);
    const r = await api("/api/admin/settings", {
      method: "PUT",
      body: JSON.stringify({ key: m.key, scope: "global", value }),
    });
    setBusy(null);
    setMsg(
      r.ok
        ? `"${m.label}" salvo.`
        : `Erro ao salvar "${m.label}": ${(r.body as any)?.message ?? r.status}`
    );
    if (r.ok) refresh();
  };

  const reset = async (m: SettingMeta) => {
    setBusy(m.key);
    setMsg(null);
    const r = await api(
      `/api/admin/settings?key=${encodeURIComponent(m.key)}&scope=global`,
      { method: "DELETE" }
    );
    setBusy(null);
    setMsg(r.ok ? `"${m.label}" voltou ao default.` : `Erro ao resetar.`);
    if (r.ok) refresh();
  };

  const categories = [...new Set(registry.map((m) => m.category))];

  return (
    <div>
      <p className="muted">
        Regras de negócio configuráveis. Valores globais; alterações valem na
        hora, sem deploy. Override por marca/produto disponível via API.
      </p>
      {msg && <p className="muted">{msg}</p>}
      {categories.map((cat) => (
        <section key={cat} style={{ marginBottom: "1.5rem" }}>
          <h3>{cat}</h3>
          <table className="data-table">
            <tbody>
              {registry
                .filter((m) => m.category === cat)
                .map((m) => {
                  const val = effectiveValue(m);
                  const overridden = !!globalOverride(m.key);
                  return (
                    <tr key={m.key}>
                      <td>
                        <strong>{m.label}</strong>
                        <div className="muted" style={{ fontSize: "0.85em" }}>
                          {m.description}
                        </div>
                        <code className="muted">{m.key}</code>
                      </td>
                      <td>
                        {m.type === "boolean" ? (
                          <label>
                            <input
                              type="checkbox"
                              checked={val === true}
                              disabled={busy === m.key}
                              onChange={(e) => save(m, e.target.checked)}
                            />{" "}
                            {val === true ? "Ligado" : "Desligado"}
                          </label>
                        ) : (
                          <select
                            value={String(val)}
                            disabled={busy === m.key}
                            onChange={(e) => save(m, e.target.value)}
                          >
                            {m.values?.map((v) => (
                              <option key={v} value={v}>
                                {v}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td>
                        {overridden ? (
                          <button
                            className="ghost-btn"
                            disabled={busy === m.key}
                            onClick={() => reset(m)}
                          >
                            Voltar ao default ({String(m.defaultValue)})
                          </button>
                        ) : (
                          <span className="muted">
                            default ({String(m.defaultValue)})
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  useEffect(() => {
    const fetch = () =>
      api<{ data: AdminUser[] }>("/api/admin/users").then(
        (r) => r.ok && setUsers(r.body.data)
      );
    fetch();
    const t = setInterval(fetch, 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>#</th>
          <th>Email</th>
          <th>Nome</th>
          <th>Stripe Customer</th>
          <th>Features (ativas/total)</th>
          <th>PIs</th>
          <th>Subs</th>
          <th>Criado</th>
        </tr>
      </thead>
      <tbody>
        {users.length === 0 ? (
          <tr><td colSpan={8} className="empty">Sem usuários.</td></tr>
        ) : (
          users.map((u) => (
            <tr key={u.id}>
              <td>{u.id}</td>
              <td>{u.email}</td>
              <td>{u.name ?? "—"}</td>
              <td>
                {u.stripeCustomerId ? (
                  <code className="muted">{u.stripeCustomerId}</code>
                ) : (
                  <em>—</em>
                )}
              </td>
              <td>
                <strong>{u.activeFeaturesCount}</strong>/{u.activationsCount}
              </td>
              <td>{u.paymentsCount}</td>
              <td>{u.subscriptionsCount}</td>
              <td>{formatDate(u.createdAt)}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

function ActivationsTab() {
  const [rows, setRows] = useState<AdminActivation[]>([]);
  useEffect(() => {
    const fetch = () =>
      api<{ data: AdminActivation[] }>("/api/admin/activations").then(
        (r) => r.ok && setRows(r.body.data)
      );
    fetch();
    const t = setInterval(fetch, 4000);
    return () => clearInterval(t);
  }, []);
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>Usuário</th>
          <th>Feature</th>
          <th>Status</th>
          <th>Origem</th>
          <th>Ativada em</th>
          <th>Desativada em</th>
          <th>Motivo</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={7} className="empty">Sem ativações.</td></tr>
        ) : (
          rows.map((a) => (
            <tr key={a.id}>
              <td>{a.userEmail ?? `#${a.userId}`}</td>
              <td><code>{a.featureKey}</code></td>
              <td><Pill status={a.status} /></td>
              <td>{a.stripeSubscriptionId ? "sub" : "one-time"}</td>
              <td>{formatDate(a.activatedAt)}</td>
              <td>{formatDate(a.deactivatedAt)}</td>
              <td>{a.deactivationReason ?? "—"}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

function SubscriptionsTab() {
  const [rows, setRows] = useState<AdminSubscription[]>([]);
  const { filters, setFilter, clear, hasAny, queryString } = useMetadataFilters();

  const refresh = useCallback(() => {
    api<{ data: AdminSubscription[] }>(
      `/api/admin/subscriptions${queryString()}`
    ).then((r) => r.ok && setRows(r.body.data));
  }, [queryString]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [refresh]);

  return (
    <>
      <MetadataFilterBar
        filters={filters}
        setFilter={setFilter}
        clear={clear}
        hasAny={hasAny}
        resultCount={rows.length}
      />
      <table className="data-table">
        <thead>
          <tr>
            <th>Usuário</th>
            <th>Items (feature_key)</th>
            <th>Origem</th>
            <th>Status</th>
            <th>Próx. cobrança</th>
            <th>Cancelada</th>
            <th>ID Stripe</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={7} className="empty">
              {hasAny ? "Sem resultados pros filtros." : "Sem assinaturas."}
            </td></tr>
          ) : (
            rows.map((s) => {
              const activeItems = s.items.filter((i) => i.status === "active");
              const canceledItems = s.items.filter((i) => i.status !== "active");
              return (
                <tr key={s.id}>
                  <td>{s.userEmail ?? `#${s.userId ?? "?"}`}</td>
                  <td>
                    {activeItems.length === 0 ? (
                      <span className="muted">—</span>
                    ) : (
                      activeItems.map((it) => (
                        <div key={it.stripeItemId}>
                          <code>{it.featureKey}</code>
                          {it.quantity > 1 && (
                            <span className="muted small"> ×{it.quantity}</span>
                          )}
                        </div>
                      ))
                    )}
                    {canceledItems.length > 0 && (
                      <div className="muted small">
                        +{canceledItems.length} item(s) cancelado(s)
                      </div>
                    )}
                  </td>
                  <td>
                    <CampaignChips
                      brand={s.brand}
                      source={s.source}
                      campaignId={s.campaignId}
                      promoCode={s.promoCode}
                      onClickFilter={setFilter}
                    />
                  </td>
                  <td><Pill status={s.status} /></td>
                  <td>{formatDate(s.currentPeriodEnd)}</td>
                  <td>{formatDate(s.canceledAt)}</td>
                  <td><code className="muted">{s.stripeSubscriptionId}</code></td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </>
  );
}

// ============================================================================
// Hook + componentes reutilizáveis para filtros canônicos (Payments / Subs / Refunds)
// ============================================================================

type MetadataFilters = {
  campaign_id: string;
  source: string;
  brand: string;
  promo_code: string;
};
type MetadataFilterKey = keyof MetadataFilters;
const EMPTY_FILTERS: MetadataFilters = {
  campaign_id: "",
  source: "",
  brand: "",
  promo_code: "",
};

function useMetadataFilters() {
  const [filters, setFilters] = useState<MetadataFilters>(EMPTY_FILTERS);
  const setFilter = useCallback((key: MetadataFilterKey, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);
  const clear = useCallback(() => setFilters(EMPTY_FILTERS), []);
  const hasAny = Object.values(filters).some((v) => v !== "");
  const queryString = useCallback(() => {
    const p = new URLSearchParams();
    (Object.keys(filters) as MetadataFilterKey[]).forEach((k) => {
      if (filters[k]) p.set(k, filters[k]);
    });
    const qs = p.toString();
    return qs ? `?${qs}` : "";
  }, [filters]);
  return { filters, setFilter, clear, hasAny, queryString };
}

function MetadataFilterBar({
  filters,
  setFilter,
  clear,
  hasAny,
  resultCount,
}: {
  filters: MetadataFilters;
  setFilter: (k: MetadataFilterKey, v: string) => void;
  clear: () => void;
  hasAny: boolean;
  resultCount: number;
}) {
  return (
    <div className="filter-bar">
      <input
        type="text"
        placeholder="campaign_id"
        value={filters.campaign_id}
        onChange={(e) => setFilter("campaign_id", e.target.value)}
      />
      <select
        value={filters.source}
        onChange={(e) => setFilter("source", e.target.value)}
      >
        <option value="">— source —</option>
        {PAYMENT_SOURCES.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
      <input
        type="text"
        placeholder="brand"
        value={filters.brand}
        onChange={(e) => setFilter("brand", e.target.value)}
      />
      <input
        type="text"
        placeholder="promo_code"
        value={filters.promo_code}
        onChange={(e) => setFilter("promo_code", e.target.value)}
      />
      {hasAny && (
        <button className="ghost-btn" onClick={clear}>Limpar</button>
      )}
      <span className="muted result-count">{resultCount} resultado(s)</span>
    </div>
  );
}

function CampaignChips({
  brand,
  source,
  campaignId,
  promoCode,
  onClickFilter,
}: {
  brand: string | null;
  source: string | null;
  campaignId: string | null;
  promoCode: string | null;
  onClickFilter: (key: MetadataFilterKey, v: string) => void;
}) {
  const items: Array<{ label: string; value: string; key: MetadataFilterKey; cls: string }> = [];
  if (brand) items.push({ label: brand, value: brand, key: "brand", cls: "chip-brand" });
  if (source) items.push({ label: source, value: source, key: "source", cls: "chip-source" });
  if (campaignId) items.push({ label: `#${campaignId}`, value: campaignId, key: "campaign_id", cls: "chip-campaign" });
  if (promoCode) items.push({ label: `promo:${promoCode}`, value: promoCode, key: "promo_code", cls: "chip-promo" });
  if (items.length === 0) return <em>—</em>;
  return (
    <div className="chip-row">
      {items.map((it) => (
        <button
          key={`${it.key}:${it.value}`}
          type="button"
          className={`chip ${it.cls}`}
          onClick={() => onClickFilter(it.key, it.value)}
          title={`Filtrar por ${it.key}=${it.value}`}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

function RefundModal({
  payment,
  onClose,
  onDone,
}: {
  payment: AdminPayment;
  onClose: () => void;
  onDone: () => void;
}) {
  const [tipo, setTipo] = useState<"total" | "parcial">("total");
  const [valorReais, setValorReais] = useState(
    (payment.amount / 100).toFixed(2)
  );
  const [reason, setReason] = useState("requested_by_customer");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    const body: { payment_intent: string; reason: string; amount?: number } = {
      payment_intent: payment.stripePaymentIntentId,
      reason,
    };
    if (tipo === "parcial") {
      const cents = Math.round(
        parseFloat(valorReais.replace(",", ".")) * 100
      );
      if (!Number.isFinite(cents) || cents <= 0) {
        setError("Valor inválido.");
        return;
      }
      if (cents > payment.amount) {
        setError("Valor maior que o pagamento.");
        return;
      }
      body.amount = cents;
    }
    setBusy(true);
    const r = await api("/api/admin/refunds", {
      method: "POST",
      body: JSON.stringify(body),
    });
    setBusy(false);
    if (r.ok) {
      onDone();
      onClose();
    } else {
      setError((r.body as any)?.error ?? `Erro HTTP ${r.status}`);
    }
  };

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Reembolsar pagamento</h3>
          <button className="modal-close" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </div>
        <div className="modal-body">
          <p>
            <strong>{payment.userEmail ?? "—"}</strong>
            <br />
            <span className="muted">
              Valor pago: {brl(payment.amount, payment.currency ?? "brl")}
            </span>
            <br />
            <code className="muted">{payment.stripePaymentIntentId}</code>
          </p>

          <label style={{ display: "block", marginTop: 12 }}>
            Tipo
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value as "total" | "parcial")}
            >
              <option value="total">Reembolso total</option>
              <option value="parcial">Parcial</option>
            </select>
          </label>

          {tipo === "parcial" && (
            <label style={{ display: "block", marginTop: 8 }}>
              Valor (R$)
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={(payment.amount / 100).toFixed(2)}
                value={valorReais}
                onChange={(e) => setValorReais(e.target.value)}
              />
            </label>
          )}

          <label style={{ display: "block", marginTop: 8 }}>
            Motivo
            <select value={reason} onChange={(e) => setReason(e.target.value)}>
              <option value="requested_by_customer">
                Solicitado pelo cliente
              </option>
              <option value="duplicate">Duplicado</option>
              <option value="fraudulent">Fraude</option>
            </select>
          </label>

          {error && (
            <p
              className="error"
              style={{ color: "var(--danger, #c00)", marginTop: 10 }}
            >
              {error}
            </p>
          )}
        </div>
        <div className="modal-footer">
          <button onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button onClick={submit} disabled={busy}>
            {busy ? "Processando…" : "Confirmar reembolso"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PaymentsTab() {
  const [rows, setRows] = useState<AdminPayment[]>([]);
  const [refundTarget, setRefundTarget] = useState<AdminPayment | null>(null);
  const { filters, setFilter, clear, hasAny, queryString } = useMetadataFilters();

  const refresh = useCallback(() => {
    api<{ data: AdminPayment[] }>(
      `/api/admin/payment-intents${queryString()}`
    ).then((r) => r.ok && setRows(r.body.data));
  }, [queryString]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [refresh]);

  return (
    <>
      <MetadataFilterBar
        filters={filters}
        setFilter={setFilter}
        clear={clear}
        hasAny={hasAny}
        resultCount={rows.length}
      />
      <table className="data-table">
        <thead>
          <tr>
            <th>Data</th>
            <th>Usuário</th>
            <th>Valor</th>
            <th>Feature</th>
            <th>Origem</th>
            <th>Status</th>
            <th>ID Stripe</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={8} className="empty">
              {hasAny ? "Sem resultados pros filtros." : "Sem pagamentos."}
            </td></tr>
          ) : (
            rows.map((p) => (
              <tr key={p.id}>
                <td>{formatDate(p.createdAt)}</td>
                <td>{p.userEmail ?? <em>—</em>}</td>
                <td>{brl(p.amount, p.currency ?? "brl")}</td>
                <td>{p.productKey ? <code>{p.productKey}</code> : "—"}</td>
                <td>
                  <CampaignChips
                    brand={p.brand}
                    source={p.source}
                    campaignId={p.campaignId}
                    promoCode={p.promoCode}
                    onClickFilter={setFilter}
                  />
                </td>
                <td><Pill status={p.status} /></td>
                <td><code className="muted">{p.stripePaymentIntentId}</code></td>
                <td>
                  <button
                    onClick={() => setRefundTarget(p)}
                    disabled={p.status !== "succeeded"}
                    title={
                      p.status !== "succeeded"
                        ? "Só pagamentos confirmados podem ser reembolsados"
                        : "Reembolsar"
                    }
                  >
                    Reembolsar
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {refundTarget && (
        <RefundModal
          payment={refundTarget}
          onClose={() => setRefundTarget(null)}
          onDone={refresh}
        />
      )}
    </>
  );
}

function RefundsTab() {
  const [rows, setRows] = useState<AdminRefund[]>([]);
  const { filters, setFilter, clear, hasAny, queryString } = useMetadataFilters();

  const refresh = useCallback(() => {
    api<{ data: AdminRefund[] }>(
      `/api/admin/refunds${queryString()}`
    ).then((r) => r.ok && setRows(r.body.data));
  }, [queryString]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [refresh]);

  return (
    <>
      <MetadataFilterBar
        filters={filters}
        setFilter={setFilter}
        clear={clear}
        hasAny={hasAny}
        resultCount={rows.length}
      />
      <table className="data-table">
        <thead>
          <tr>
            <th>Data</th>
            <th>Usuário</th>
            <th>Valor</th>
            <th>Feature</th>
            <th>Origem</th>
            <th>Motivo</th>
            <th>Status</th>
            <th>PI</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={8} className="empty">
              {hasAny ? "Sem resultados pros filtros." : "Sem reembolsos."}
            </td></tr>
          ) : (
            rows.map((r) => (
              <tr key={r.id}>
                <td>{formatDate(r.createdAt)}</td>
                <td>{r.userEmail ?? <em>—</em>}</td>
                <td>{brl(r.amount)}</td>
                <td>{r.productKey ? <code>{r.productKey}</code> : "—"}</td>
                <td>
                  <CampaignChips
                    brand={r.brand}
                    source={r.source}
                    campaignId={r.campaignId}
                    promoCode={r.promoCode}
                    onClickFilter={setFilter}
                  />
                </td>
                <td>{r.reason ?? "—"}</td>
                <td><Pill status={r.status} /></td>
                <td><code className="muted">{r.stripePaymentIntentId ?? "—"}</code></td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </>
  );
}

// ============================================================================
// CATÁLOGO — produtos vendáveis (feature_catalog)
// ============================================================================

type FeatureCatalogRow = {
  id: number;
  featureKey: string;
  name: string;
  description: string;
  mode: "one-time" | "subscription";
  amountCents: number;
  currency: string;
  recurringInterval: "day" | "week" | "month" | "year" | null;
  checkoutMode: "inline" | "hosted" | "elements";
  stripeLookupKey: string | null;
  stripeProductId: string | null;
  stripePriceId: string | null;
  defaultBrand: string | null;
  allowRepurchase: boolean;
  seatable: boolean;
  g4membersProductId: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};

type CreateCatalogInput = {
  feature_key: string;
  name: string;
  description: string;
  mode: "one-time" | "subscription";
  amount_cents: number;
  currency: string;
  recurring_interval?: "day" | "week" | "month" | "year";
  checkout_mode: "inline" | "hosted" | "elements";
  stripe_lookup_key?: string;
  default_brand?: string;
  allow_repurchase?: boolean;
  seatable?: boolean;
};

type BrandOption = { slug: string; name: string };

// Eixo "Compra múltipla" mutuamente exclusivo. Mapeia pras duas booleanas do
// backend: repurchase -> allow_repurchase, seatable -> seatable. seatable só
// vale em sub (UI esconde a opção; backend valida).
type MultiPurchase = "none" | "repurchase" | "seatable";

function multiPurchaseFrom(
  allowRepurchase: boolean,
  seatable: boolean
): MultiPurchase {
  if (seatable) return "seatable";
  if (allowRepurchase) return "repurchase";
  return "none";
}

function MultiPurchaseSelect({
  mode,
  value,
  onChange,
  disabled,
}: {
  mode: "one-time" | "subscription";
  value: MultiPurchase;
  onChange: (v: MultiPurchase) => void;
  disabled?: boolean;
}) {
  return (
    <label className="full-width">
      Compra múltipla
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as MultiPurchase)}
        disabled={disabled}
      >
        <option value="none">não permite (1 compra por feature)</option>
        <option value="repurchase">
          repurchase (compras separadas: doação, coins, créditos)
        </option>
        {mode === "subscription" && (
          <option value="seatable">
            seatable (quantidade no mesmo item: assentos)
          </option>
        )}
      </select>
    </label>
  );
}

function CatalogTab() {
  const [rows, setRows] = useState<FeatureCatalogRow[]>([]);
  const [brands, setBrands] = useState<BrandOption[]>([]);
  const [unimportedCount, setUnimportedCount] = useState(0);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [versioningId, setVersioningId] = useState<number | null>(null);
  const [relationsId, setRelationsId] = useState<number | null>(null);
  // Produto aberto na pagina de detalhe (estilo Hotmart: produto = container das
  // ofertas). null = lista normal.
  const [detailId, setDetailId] = useState<number | null>(null);

  const refresh = useCallback(() => {
    api<{ data: FeatureCatalogRow[] }>("/api/admin/feature-catalog").then(
      (r) => r.ok && setRows(r.body.data)
    );
    // Conta produtos no Stripe que ainda não estão no catálogo — sinal pra
    // mostrar badge no botão "Importar do Stripe".
    api<{ data: Array<{ already_imported: boolean }> }>(
      "/api/admin/feature-catalog/stripe-prices"
    ).then((r) => {
      if (r.ok) {
        setUnimportedCount(
          r.body.data.filter((p) => !p.already_imported).length
        );
      }
    });
  }, []);

  useEffect(() => {
    refresh();
    api<{ data: Array<{ slug: string; name: string; active: boolean }> }>(
      "/api/admin/service-clients"
    ).then((r) => {
      if (r.ok) {
        setBrands(
          r.body.data
            .filter((b) => b.active)
            .map((b) => ({ slug: b.slug, name: b.name }))
        );
      }
    });
  }, [refresh]);

  const handleCreate = async (input: CreateCatalogInput) => {
    const r = await api<FeatureCatalogRow>("/api/admin/feature-catalog", {
      method: "POST",
      body: JSON.stringify(input),
    });
    if (r.ok) {
      setCreating(false);
      refresh();
    } else {
      alert(`Erro (HTTP ${r.status}): ${JSON.stringify(r.body)}`);
    }
  };

  const handleToggleActive = async (row: FeatureCatalogRow) => {
    await api(`/api/admin/feature-catalog/${row.id}`, {
      method: "PATCH",
      body: JSON.stringify({ active: !row.active }),
    });
    refresh();
  };

  const handleSaveEdit = async (
    id: number,
    patch: {
      name: string;
      description: string;
      default_brand: string | null;
      allow_repurchase: boolean;
      seatable: boolean;
      g4members_product_id: string | null;
    }
  ) => {
    const r = await api(`/api/admin/feature-catalog/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
    if (r.ok) {
      setEditingId(null);
      refresh();
    } else {
      alert(`Erro (HTTP ${r.status}): ${JSON.stringify(r.body)}`);
    }
  };

  const handleVersionPrice = async (
    id: number,
    amountCents: number,
    archiveOld: boolean
  ) => {
    const r = await api<{
      old_price_id: string;
      new_price_id: string;
      old_archived: boolean;
    }>(`/api/admin/feature-catalog/${id}/new-price`, {
      method: "POST",
      body: JSON.stringify({
        amount_cents: amountCents,
        archive_old: archiveOld,
      }),
    });
    if (r.ok) {
      setVersioningId(null);
      refresh();
      alert(
        `Price novo criado: ${r.body.new_price_id}\nAntigo: ${r.body.old_price_id} (${r.body.old_archived ? "arquivado" : "ainda ativo"})\nSubs existentes seguem cobrando o antigo até serem migradas.`
      );
    } else {
      alert(`Erro (HTTP ${r.status}): ${JSON.stringify(r.body)}`);
    }
  };

  // Pagina de detalhe do produto (produto = container das ofertas/bumps/preço).
  const detailRow =
    detailId != null ? rows.find((r) => r.id === detailId) ?? null : null;
  if (detailRow) {
    return (
      <ProductDetailView
        row={detailRow}
        brands={brands}
        allRows={rows}
        onBack={() => setDetailId(null)}
        onSaveEdit={handleSaveEdit}
        onVersionPrice={handleVersionPrice}
        onToggleActive={handleToggleActive}
        onRefresh={refresh}
      />
    );
  }

  return (
    <>
      <div className="tab-toolbar">
        {!creating && !importing && (
          <>
            <button className="primary-btn" onClick={() => setCreating(true)}>
              + Novo produto
            </button>
            <button
              className={unimportedCount > 0 ? "primary-btn" : "ghost-btn"}
              onClick={() => setImporting(true)}
            >
              Importar do Stripe
              {unimportedCount > 0 && (
                <span
                  style={{
                    marginLeft: 6,
                    padding: "1px 8px",
                    borderRadius: 999,
                    background: "rgba(255,255,255,0.25)",
                    fontSize: "0.85em",
                  }}
                >
                  {unimportedCount} novo{unimportedCount > 1 ? "s" : ""}
                </span>
              )}
            </button>
          </>
        )}
      </div>

      {creating && (
        <CreateCatalogForm
          brands={brands}
          onCancel={() => setCreating(false)}
          onSubmit={handleCreate}
        />
      )}

      {importing && (
        <ImportStripeSection
          brands={brands}
          onClose={() => setImporting(false)}
          onImported={refresh}
        />
      )}

      <table className="data-table">
        <thead>
          <tr>
            <th>#</th>
            <th>feature_key</th>
            <th>Nome</th>
            <th>Modo</th>
            <th>Preço</th>
            <th>Checkout</th>
            <th>Brand</th>
            <th>Stripe</th>
            <th>Status</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={10} className="empty">
                Sem produtos.
              </td>
            </tr>
          ) : (
            rows.map((row) =>
              editingId === row.id ? (
                <tr key={row.id} className="row-expanded">
                  <td colSpan={10}>
                    <EditCatalogForm
                      brands={brands}
                      initial={{
                        name: row.name,
                        description: row.description,
                        default_brand: row.defaultBrand,
                        allow_repurchase: row.allowRepurchase,
                        seatable: row.seatable,
                        g4members_product_id: row.g4membersProductId,
                        mode: row.mode,
                      }}
                      onCancel={() => setEditingId(null)}
                      onSubmit={(patch) => handleSaveEdit(row.id, patch)}
                    />
                  </td>
                </tr>
              ) : versioningId === row.id ? (
                <tr key={row.id} className="row-expanded">
                  <td colSpan={10}>
                    <VersionPriceForm
                      row={row}
                      onCancel={() => setVersioningId(null)}
                      onSubmit={(amountCents, archiveOld) =>
                        handleVersionPrice(row.id, amountCents, archiveOld)
                      }
                    />
                  </td>
                </tr>
              ) : relationsId === row.id ? (
                <tr key={row.id} className="row-expanded">
                  <td colSpan={10}>
                    <RelationsForm
                      row={row}
                      allRows={rows}
                      onCancel={() => setRelationsId(null)}
                      onSaved={() => {
                        setRelationsId(null);
                        refresh();
                      }}
                    />
                  </td>
                </tr>
              ) : (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>
                    <code>{row.featureKey}</code>
                  </td>
                  <td>
                    <button
                      className="link-btn"
                      onClick={() => setDetailId(row.id)}
                      title="Abrir produto (ofertas, bumps, preço)"
                    >
                      {row.name}
                    </button>
                    <div className="muted" style={{ fontSize: "0.85em" }}>
                      {row.description}
                    </div>
                  </td>
                  <td>
                    {row.mode === "subscription"
                      ? `sub / ${row.recurringInterval}`
                      : "one-time"}
                  </td>
                  <td>{brl(row.amountCents)}</td>
                  <td>
                    <code>{row.checkoutMode}</code>
                  </td>
                  <td>
                    {row.defaultBrand ? (
                      <code>{row.defaultBrand}</code>
                    ) : (
                      <em className="muted">padrão icl</em>
                    )}
                  </td>
                  <td>
                    {row.stripePriceId ? (
                      <code className="muted" style={{ fontSize: "0.8em" }}>
                        {row.stripePriceId}
                      </code>
                    ) : null}
                  </td>
                  <td>
                    <Pill status={row.active ? "active" : "inactive"} />
                  </td>
                  <td>
                    <RowActions>
                      <button
                        className="ghost-btn"
                        onClick={() => setDetailId(row.id)}
                        title="Abrir produto (ofertas, bumps, preço)"
                      >
                        Abrir
                      </button>
                      <button onClick={() => setEditingId(row.id)}>
                        Editar
                      </button>
                      <button
                        onClick={() => setRelationsId(row.id)}
                        title="Bumps e upsells oferecidos junto deste produto"
                      >
                        Bumps/Upsells
                      </button>
                    {row.stripeProductId && row.stripePriceId && (
                      <button
                        onClick={() => setVersioningId(row.id)}
                        title="Cria Price novo sob mesmo Product; subs existentes seguem no antigo"
                      >
                        Atualizar preço
                      </button>
                    )}
                      <button onClick={() => handleToggleActive(row)}>
                        {row.active ? "Desativar" : "Ativar"}
                      </button>
                    </RowActions>
                  </td>
                </tr>
              )
            )
          )}
        </tbody>
      </table>
    </>
  );
}

// Wrapper de ações por linha: ação primária "Editar" sempre visível + menu
// "Mais" com as ações secundárias. Filhos esperados: 1º child é a ação primária
// (renderizada inline como ghost-btn), os demais ficam no dropdown.
function RowActions({ children }: { children: ReactNode }) {
  // Conditional children (ex: {x && <button/>}) podem virar `false`/null —
  // filtra pra não vazar slot vazio no menu.
  const arr = (Array.isArray(children) ? children : [children]).filter(
    (c) => c !== null && c !== undefined && c !== false
  );
  const [primary, ...rest] = arr;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    <div className="row-actions" ref={ref}>
      <span className="row-actions-primary">{primary}</span>
      {rest.length > 0 && (
        <>
          <button
            type="button"
            className="ghost-btn row-actions-toggle"
            onClick={() => setOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={open}
            title="Mais ações"
          >
            ⋯
          </button>
          {open && (
            <div className="row-actions-menu" onClick={() => setOpen(false)}>
              {rest}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Pagina de detalhe do produto (estilo Hotmart): o produto e' o container e as
// ofertas/bumps/preço vivem DENTRO dele, em seções. Reusa os forms inline da
// lista (EditCatalogForm/VersionPriceForm/RelationsForm) + embute a OffersTab
// ja filtrada pelo feature_key do produto.
function ProductDetailView({
  row,
  brands,
  allRows,
  onBack,
  onSaveEdit,
  onVersionPrice,
  onToggleActive,
  onRefresh,
}: {
  row: FeatureCatalogRow;
  brands: BrandOption[];
  allRows: FeatureCatalogRow[];
  onBack: () => void;
  onSaveEdit: (
    id: number,
    patch: {
      name: string;
      description: string;
      default_brand: string | null;
      allow_repurchase: boolean;
      seatable: boolean;
      g4members_product_id: string | null;
    }
  ) => Promise<void>;
  onVersionPrice: (
    id: number,
    amountCents: number,
    archiveOld: boolean
  ) => Promise<void>;
  onToggleActive: (row: FeatureCatalogRow) => void;
  onRefresh: () => void;
}) {
  const [section, setSection] = useState<"dados" | "ofertas" | "bumps">("dados");
  const [editing, setEditing] = useState(false);
  const [versioning, setVersioning] = useState(false);

  return (
    <div className="product-detail">
      <div className="tab-toolbar">
        <button className="ghost-btn" onClick={onBack}>
          ← Catálogo
        </button>
      </div>

      <header className="page-header" style={{ marginTop: 8 }}>
        <h2 style={{ margin: 0 }}>{row.name}</h2>
        <p className="hint">
          <code>{row.featureKey}</code>
          {" · "}
          {row.mode === "subscription"
            ? `assinatura / ${row.recurringInterval}`
            : "pagamento único"}
          {" · "}
          {brl(row.amountCents)}
          {" · "}
          {row.defaultBrand ? (
            <code>{row.defaultBrand}</code>
          ) : (
            <em className="muted">padrão icl</em>
          )}{" "}
          <Pill status={row.active ? "active" : "inactive"} />
        </p>
      </header>

      <nav className="tabs">
        <button
          className={section === "dados" ? "tab active" : "tab"}
          onClick={() => setSection("dados")}
        >
          Dados &amp; Preço
        </button>
        <button
          className={section === "ofertas" ? "tab active" : "tab"}
          onClick={() => setSection("ofertas")}
        >
          Ofertas
        </button>
        <button
          className={section === "bumps" ? "tab active" : "tab"}
          onClick={() => setSection("bumps")}
        >
          Bumps/Upsells
        </button>
      </nav>

      <div className="tab-content">
        {section === "dados" &&
          (editing ? (
            <EditCatalogForm
              brands={brands}
              initial={{
                name: row.name,
                description: row.description,
                default_brand: row.defaultBrand,
                allow_repurchase: row.allowRepurchase,
                seatable: row.seatable,
                g4members_product_id: row.g4membersProductId,
                mode: row.mode,
              }}
              onCancel={() => setEditing(false)}
              onSubmit={async (patch) => {
                await onSaveEdit(row.id, patch);
                setEditing(false);
              }}
            />
          ) : versioning ? (
            <VersionPriceForm
              row={row}
              onCancel={() => setVersioning(false)}
              onSubmit={async (amountCents, archiveOld) => {
                await onVersionPrice(row.id, amountCents, archiveOld);
                setVersioning(false);
              }}
            />
          ) : (
            <>
              <dl className="detail-grid">
                <div>
                  <dt>Descrição</dt>
                  <dd>{row.description}</dd>
                </div>
                <div>
                  <dt>Checkout</dt>
                  <dd>
                    <code>{row.checkoutMode}</code>
                  </dd>
                </div>
                <div>
                  <dt>Stripe Price</dt>
                  <dd>
                    {row.stripePriceId ? (
                      <code className="muted">{row.stripePriceId}</code>
                    ) : (
                      <em className="muted">—</em>
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Recompra</dt>
                  <dd>{row.allowRepurchase ? "permitida" : "não"}</dd>
                </div>
                {row.mode === "subscription" && (
                  <div>
                    <dt>Assentos</dt>
                    <dd>{row.seatable ? "sim (quantidade)" : "não"}</dd>
                  </div>
                )}
              </dl>
              <div className="form-actions">
                <button className="primary-btn" onClick={() => setEditing(true)}>
                  Editar
                </button>
                {row.stripeProductId && row.stripePriceId && (
                  <button
                    className="ghost-btn"
                    onClick={() => setVersioning(true)}
                    title="Cria Price novo sob mesmo Product; subs existentes seguem no antigo"
                  >
                    Atualizar preço
                  </button>
                )}
                <button className="ghost-btn" onClick={() => onToggleActive(row)}>
                  {row.active ? "Desativar" : "Ativar"}
                </button>
              </div>
            </>
          ))}

        {section === "ofertas" && <OffersTab initialFeatureKey={row.featureKey} />}

        {section === "bumps" && (
          <RelationsForm
            row={row}
            allRows={allRows}
            onCancel={() => setSection("dados")}
            onSaved={onRefresh}
          />
        )}
      </div>
    </div>
  );
}

function CreateCatalogForm({
  brands,
  onCancel,
  onSubmit,
}: {
  brands: BrandOption[];
  onCancel: () => void;
  onSubmit: (input: CreateCatalogInput) => Promise<void>;
}) {
  const [featureKey, setFeatureKey] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [mode, setMode] = useState<"one-time" | "subscription">("one-time");
  const [amountReais, setAmountReais] = useState("");
  const [currency, setCurrency] = useState("brl");
  const [recurringInterval, setRecurringInterval] = useState<
    "day" | "week" | "month" | "year"
  >("month");
  const [checkoutMode, setCheckoutMode] = useState<
    "inline" | "hosted" | "elements"
  >("hosted");
  const [stripeLookupKey, setStripeLookupKey] = useState("");
  const [defaultBrand, setDefaultBrand] = useState<string>("");
  const [multiPurchase, setMultiPurchase] = useState<MultiPurchase>("none");
  const [submitting, setSubmitting] = useState(false);

  return (
    <form
      className="inline-form"
      onSubmit={async (e) => {
        e.preventDefault();
        const amountCents = Math.round(parseFloat(amountReais) * 100);
        if (!Number.isFinite(amountCents) || amountCents <= 0) {
          alert("Preço inválido");
          return;
        }
        setSubmitting(true);
        await onSubmit({
          feature_key: featureKey,
          name,
          description,
          mode,
          amount_cents: amountCents,
          currency,
          checkout_mode: checkoutMode,
          ...(defaultBrand && { default_brand: defaultBrand }),
          ...(multiPurchase === "repurchase" && { allow_repurchase: true }),
          ...(multiPurchase === "seatable" && { seatable: true }),
          ...(mode === "subscription" && {
            recurring_interval: recurringInterval,
            stripe_lookup_key: stripeLookupKey,
          }),
        });
        setSubmitting(false);
      }}
    >
      <label>
        feature_key (snake_case)
        <input
          required
          pattern="^[a-z0-9_]+$"
          value={featureKey}
          onChange={(e) => setFeatureKey(e.target.value)}
          placeholder="curso_xyz_2026"
          disabled={submitting}
          autoFocus
        />
      </label>
      <label>
        Nome
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Curso XYZ 2026"
          disabled={submitting}
        />
      </label>
      <label className="full-width">
        Descrição
        <textarea
          required
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Descrição visível na storefront"
          disabled={submitting}
        />
      </label>
      <label>
        Modo
        <select
          value={mode}
          onChange={(e) => {
            const next = e.target.value as "one-time" | "subscription";
            setMode(next);
            if (next !== "subscription" && multiPurchase === "seatable") {
              setMultiPurchase("none");
            }
          }}
          disabled={submitting}
        >
          <option value="one-time">one-time</option>
          <option value="subscription">subscription</option>
        </select>
      </label>
      <label>
        Preço (R$)
        <input
          required
          type="number"
          step="0.01"
          min="0.01"
          value={amountReais}
          onChange={(e) => setAmountReais(e.target.value)}
          placeholder="197.00"
          disabled={submitting}
        />
      </label>
      <label>
        Currency
        <input
          required
          value={currency}
          onChange={(e) => setCurrency(e.target.value.toLowerCase())}
          maxLength={3}
          disabled={submitting}
        />
      </label>
      <label>
        Checkout
        <select
          value={checkoutMode}
          onChange={(e) =>
            setCheckoutMode(
              e.target.value as "inline" | "hosted" | "elements"
            )
          }
          disabled={submitting}
        >
          <option value="hosted">hosted (Stripe Checkout)</option>
          <option value="inline">inline (PI confirm, card-only)</option>
          <option value="elements">elements (custom, embutido no site)</option>
        </select>
      </label>
      <label>
        Brand
        <select
          value={defaultBrand}
          onChange={(e) => setDefaultBrand(e.target.value)}
          disabled={submitting}
        >
          <option value="">padrão icl</option>
          {brands.map((b) => (
            <option key={b.slug} value={b.slug}>
              {b.name} ({b.slug})
            </option>
          ))}
        </select>
      </label>
      {mode === "subscription" && (
        <>
          <label>
            Intervalo
            <select
              value={recurringInterval}
              onChange={(e) =>
                setRecurringInterval(
                  e.target.value as "day" | "week" | "month" | "year"
                )
              }
              disabled={submitting}
            >
              <option value="day">day</option>
              <option value="week">week</option>
              <option value="month">month</option>
              <option value="year">year</option>
            </select>
          </label>
          <label>
            stripe_lookup_key
            <input
              required
              value={stripeLookupKey}
              onChange={(e) => setStripeLookupKey(e.target.value)}
              placeholder="curso_xyz_2026_monthly_v1"
              disabled={submitting}
            />
          </label>
        </>
      )}
      <MultiPurchaseSelect
        mode={mode}
        value={multiPurchase}
        onChange={setMultiPurchase}
        disabled={submitting}
      />
      <div className="form-actions">
        <button type="submit" className="primary-btn" disabled={submitting}>
          {submitting ? "Criando..." : "Criar"}
        </button>
        <button
          type="button"
          className="ghost-btn"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

function EditCatalogForm({
  brands,
  initial,
  onCancel,
  onSubmit,
}: {
  brands: BrandOption[];
  initial: {
    name: string;
    description: string;
    default_brand: string | null;
    allow_repurchase: boolean;
    seatable: boolean;
    g4members_product_id: string | null;
    mode: "one-time" | "subscription";
  };
  onCancel: () => void;
  onSubmit: (patch: {
    name: string;
    description: string;
    default_brand: string | null;
    allow_repurchase: boolean;
    seatable: boolean;
    g4members_product_id: string | null;
  }) => Promise<void>;
}) {
  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description);
  const [defaultBrand, setDefaultBrand] = useState<string>(
    initial.default_brand ?? ""
  );
  const [multiPurchase, setMultiPurchase] = useState<MultiPurchase>(
    multiPurchaseFrom(initial.allow_repurchase, initial.seatable)
  );
  const [g4membersProductId, setG4membersProductId] = useState<string>(
    initial.g4members_product_id ?? ""
  );
  const [submitting, setSubmitting] = useState(false);

  return (
    <form
      className="inline-form"
      onSubmit={async (e) => {
        e.preventDefault();
        setSubmitting(true);
        await onSubmit({
          name,
          description,
          default_brand: defaultBrand === "" ? null : defaultBrand,
          allow_repurchase: multiPurchase === "repurchase",
          seatable: multiPurchase === "seatable",
          g4members_product_id:
            g4membersProductId.trim() === "" ? null : g4membersProductId.trim(),
        });
        setSubmitting(false);
      }}
    >
      <label>
        Nome
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={submitting}
          autoFocus
        />
      </label>
      <label>
        Brand
        <select
          value={defaultBrand}
          onChange={(e) => setDefaultBrand(e.target.value)}
          disabled={submitting}
        >
          <option value="">padrão icl</option>
          {brands.map((b) => (
            <option key={b.slug} value={b.slug}>
              {b.name} ({b.slug})
            </option>
          ))}
        </select>
      </label>
      <label className="full-width">
        Descrição
        <textarea
          required
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={submitting}
        />
      </label>
      <MultiPurchaseSelect
        mode={initial.mode}
        value={multiPurchase}
        onChange={setMultiPurchase}
        disabled={submitting}
      />
      <label>
        g4members product_id (opcional)
        <input
          value={g4membersProductId}
          onChange={(e) => setG4membersProductId(e.target.value)}
          placeholder="ex: 5854418 (vazio = não entrega ao g4members)"
          disabled={submitting}
        />
      </label>
      <div className="form-actions">
        <button type="submit" className="primary-btn" disabled={submitting}>
          {submitting ? "Salvando..." : "Salvar"}
        </button>
        <button
          type="button"
          className="ghost-btn"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

function VersionPriceForm({
  row,
  onCancel,
  onSubmit,
}: {
  row: FeatureCatalogRow;
  onCancel: () => void;
  onSubmit: (amountCents: number, archiveOld: boolean) => Promise<void>;
}) {
  const [amountReais, setAmountReais] = useState(
    (row.amountCents / 100).toFixed(2)
  );
  const [archiveOld, setArchiveOld] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  return (
    <form
      className="inline-form"
      onSubmit={async (e) => {
        e.preventDefault();
        const amountCents = Math.round(parseFloat(amountReais) * 100);
        if (!Number.isFinite(amountCents) || amountCents <= 0) {
          alert("Preço inválido");
          return;
        }
        if (amountCents === row.amountCents) {
          alert("Preço igual ao atual");
          return;
        }
        setSubmitting(true);
        await onSubmit(amountCents, archiveOld);
        setSubmitting(false);
      }}
    >
      <p className="hint full-width">
        Cria Price novo sob o mesmo Stripe Product. Subs e PIs existentes
        seguem cobrando o Price antigo (referenciam por ID). Lookup_key (se
        houver) é transferido pro novo automaticamente; integrações que
        resolvem por lookup pegam o novo na próxima request.
      </p>
      <label>
        Preço atual
        <input value={brl(row.amountCents)} disabled />
      </label>
      <label>
        Novo preço (R$)
        <input
          required
          type="number"
          step="0.01"
          min="0.01"
          value={amountReais}
          onChange={(e) => setAmountReais(e.target.value)}
          disabled={submitting}
          autoFocus
        />
      </label>
      <label className="full-width">
        <input
          type="checkbox"
          checked={archiveOld}
          onChange={(e) => setArchiveOld(e.target.checked)}
          disabled={submitting}
        />{" "}
        Arquivar Price antigo (recomendado: esconde da loja, mas não afeta
        cobranças em curso)
      </label>
      <div className="form-actions">
        <button type="submit" className="primary-btn" disabled={submitting}>
          {submitting ? "Criando..." : "Criar Price novo"}
        </button>
        <button
          type="button"
          className="ghost-btn"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

// ============================================================================
// RELAÇÕES (bumps + upsells) — config admin granular por produto
// ============================================================================

type RelationRow = {
  id: number;
  bump_feature_key?: string;
  upsell_feature_key?: string;
  position: number;
  active: boolean;
};

function isHostedAndActive(row: FeatureCatalogRow): boolean {
  return row.checkoutMode === "hosted" && row.active;
}

// Bumps elegíveis pra um produto principal. Filtros aplicados:
//  - hosted + ativo (Stripe Checkout só, não inline)
//  - ≠ próprio produto (sem self-ref)
//  - One-time bump: sempre OK
//  - Sub bump: só se principal é sub do MESMO interval (constraint Stripe)
function eligibleBumpsFor(
  parent: FeatureCatalogRow,
  all: FeatureCatalogRow[]
): FeatureCatalogRow[] {
  if (parent.checkoutMode !== "hosted") return [];
  return all.filter((b) => {
    if (b.id === parent.id) return false;
    if (!isHostedAndActive(b)) return false;
    if (b.mode === "one-time") return true;
    return (
      parent.mode === "subscription" &&
      !!b.stripePriceId &&
      !!b.recurringInterval &&
      b.recurringInterval === parent.recurringInterval
    );
  });
}

// Upsells elegíveis: any hosted active product ≠ self. Sub upsell exige price_id
// (criação off-session via /api/upsell-charges depende dele).
function eligibleUpsellsFor(
  parent: FeatureCatalogRow,
  all: FeatureCatalogRow[]
): FeatureCatalogRow[] {
  return all.filter((b) => {
    if (b.id === parent.id) return false;
    if (!isHostedAndActive(b)) return false;
    if (b.mode === "subscription" && !b.stripePriceId) return false;
    return true;
  });
}

function RelationsForm({
  row,
  allRows,
  onCancel,
  onSaved,
}: {
  row: FeatureCatalogRow;
  allRows: FeatureCatalogRow[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [bumps, setBumps] = useState<string[]>([]);
  const [upsells, setUpsells] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bumpToAdd, setBumpToAdd] = useState("");
  const [upsellToAdd, setUpsellToAdd] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api<{ data: RelationRow[] }>(
        `/api/admin/feature-catalog/${row.featureKey}/bumps`
      ),
      api<{ data: RelationRow[] }>(
        `/api/admin/feature-catalog/${row.featureKey}/upsells`
      ),
    ]).then(([bRes, uRes]) => {
      if (cancelled) return;
      if (bRes.ok) {
        setBumps(
          bRes.body.data
            .filter((r) => r.active && r.bump_feature_key)
            .map((r) => r.bump_feature_key!)
        );
      }
      if (uRes.ok) {
        setUpsells(
          uRes.body.data
            .filter((r) => r.active && r.upsell_feature_key)
            .map((r) => r.upsell_feature_key!)
        );
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [row.featureKey]);

  const handleSave = async () => {
    setSubmitting(true);
    const bumpRes = await api(
      `/api/admin/feature-catalog/${row.featureKey}/bumps`,
      {
        method: "PUT",
        body: JSON.stringify({ feature_keys: bumps }),
      }
    );
    const upsellRes = await api(
      `/api/admin/feature-catalog/${row.featureKey}/upsells`,
      {
        method: "PUT",
        body: JSON.stringify({ feature_keys: upsells }),
      }
    );
    setSubmitting(false);
    if (!bumpRes.ok || !upsellRes.ok) {
      alert(
        `Falhou: bumps=${bumpRes.status}, upsells=${upsellRes.status}\n` +
          JSON.stringify(bumpRes.ok ? upsellRes.body : bumpRes.body)
      );
      return;
    }
    onSaved();
  };

  const move = (list: string[], i: number, dir: -1 | 1): string[] => {
    const next = [...list];
    const j = i + dir;
    if (j < 0 || j >= next.length) return list;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  };

  const bumpCandidates = eligibleBumpsFor(row, allRows);
  const upsellCandidates = eligibleUpsellsFor(row, allRows);

  if (loading) {
    return (
      <p className="muted" style={{ padding: "1em" }}>
        Carregando relações…
      </p>
    );
  }

  const renderList = (
    label: string,
    list: string[],
    setList: (l: string[]) => void,
    add: string,
    setAdd: (v: string) => void,
    candidates: FeatureCatalogRow[],
    hint: string
  ) => {
    const candidatesByKey = new Map(candidates.map((c) => [c.featureKey, c]));
    return (
      <fieldset
        style={{
          border: "1px solid #ddd",
          borderRadius: 6,
          padding: "0.75em",
          marginBottom: "1em",
        }}
      >
        <legend>
          <strong>{label}</strong>
        </legend>
        <p className="hint">{hint}</p>
        {list.length === 0 ? (
          <p className="muted" style={{ fontSize: "0.9em" }}>
            Nenhum configurado. Storefront/success usam fallback automático
            (todos elegíveis).
          </p>
        ) : (
          <ol style={{ paddingLeft: "1.5em" }}>
            {list.map((k, i) => {
              const meta = candidatesByKey.get(k) ?? allRows.find((r) => r.featureKey === k);
              return (
                <li key={k} style={{ marginBottom: "0.4em" }}>
                  <code>{k}</code>
                  {meta && (
                    <span className="muted" style={{ marginLeft: "0.5em" }}>
                      {meta.name} ({brl(meta.amountCents)})
                    </span>
                  )}
                  <span style={{ marginLeft: "0.5em" }}>
                    <button
                      type="button"
                      className="ghost-btn"
                      disabled={i === 0 || submitting}
                      onClick={() => setList(move(list, i, -1))}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="ghost-btn"
                      disabled={i === list.length - 1 || submitting}
                      onClick={() => setList(move(list, i, 1))}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="ghost-btn"
                      disabled={submitting}
                      onClick={() => setList(list.filter((x) => x !== k))}
                    >
                      Remover
                    </button>
                  </span>
                </li>
              );
            })}
          </ol>
        )}
        <div style={{ display: "flex", gap: "0.5em", marginTop: "0.5em" }}>
          <select
            value={add}
            onChange={(e) => setAdd(e.target.value)}
            disabled={submitting}
            style={{ flex: 1 }}
          >
            <option value="">escolher produto...</option>
            {candidates
              .filter((c) => !list.includes(c.featureKey))
              .map((c) => (
                <option key={c.featureKey} value={c.featureKey}>
                  {c.featureKey} · {c.name} ({brl(c.amountCents)}
                  {c.mode === "subscription" ? `/${c.recurringInterval}` : ""})
                </option>
              ))}
          </select>
          <button
            type="button"
            className="ghost-btn"
            disabled={!add || submitting}
            onClick={() => {
              setList([...list, add]);
              setAdd("");
            }}
          >
            + Adicionar
          </button>
        </div>
      </fieldset>
    );
  };

  return (
    <form
      className="inline-form"
      onSubmit={(e) => {
        e.preventDefault();
        handleSave();
      }}
    >
      <p className="hint full-width">
        Configura quais produtos aparecem como <strong>order bump</strong>{" "}
        (checkbox no card da loja) e como <strong>upsell pós-compra</strong> (na
        Success page) quando o usuário compra <code>{row.featureKey}</code>.
        Ordem da lista controla a posição de exibição. Listas vazias caem no
        fallback automático.
      </p>

      <div className="full-width">
        {renderList(
          "Order bumps",
          bumps,
          setBumps,
          bumpToAdd,
          setBumpToAdd,
          bumpCandidates,
          row.mode === "subscription"
            ? `Bumps one-time são adicionados como InvoiceItems na primeira fatura da sub. Bumps sub só elegíveis se mesmo recurring_interval (${row.recurringInterval ?? "?"}); constraint Stripe.`
            : "Bumps sub não são oferecidos em produto one-time (Stripe não aceita mixar mode=payment com line_items recorrentes)."
        )}

        {renderList(
          "Upsells pós-compra",
          upsells,
          setUpsells,
          upsellToAdd,
          setUpsellToAdd,
          upsellCandidates,
          "Cobrança one-click off-session no cartão usado. Upsell sub exige stripe_price_id (resolução automática para subs hosted)."
        )}
      </div>

      <div className="form-actions full-width">
        <button type="submit" className="primary-btn" disabled={submitting}>
          {submitting ? "Salvando…" : "Salvar"}
        </button>
        <button
          type="button"
          className="ghost-btn"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

// ============================================================================
// IMPORTAR DO STRIPE — lista Prices ativos e permite trazer pro catálogo local
// ============================================================================

type StripePriceForImport = {
  price_id: string;
  product_id: string;
  name: string;
  description: string;
  amount_cents: number;
  currency: string;
  mode: "one-time" | "subscription";
  recurring_interval: "day" | "week" | "month" | "year" | null;
  lookup_key: string | null;
  already_imported: boolean;
  imported_feature_key: string | null;
  // Sugestão de brand vinda de stripe_product.metadata.brand — só
  // populada se o slug existir em service_clients ativas.
  stripe_brand_hint: string | null;
  // Valor cru de metadata.brand (mesmo se inválido), pra warn na UI.
  stripe_brand_raw: string | null;
  // Unix seconds, pra ordenação por mais recente primeiro.
  price_created: number;
  product_created: number;
};

function slugifyFeatureKey(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

function ImportStripeSection({
  brands,
  onClose,
  onImported,
}: {
  brands: BrandOption[];
  onClose: () => void;
  onImported: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [prices, setPrices] = useState<StripePriceForImport[]>([]);
  const [drafts, setDrafts] = useState<
    Record<
      string,
      {
        feature_key: string;
        checkout_mode: "inline" | "hosted";
        default_brand: string;
      }
    >
  >({});
  const [submitting, setSubmitting] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    api<{ data: StripePriceForImport[]; has_more: boolean }>(
      "/api/admin/feature-catalog/stripe-prices"
    ).then((r) => {
      setLoading(false);
      if (!r.ok) {
        setError(`HTTP ${r.status}: ${JSON.stringify(r.body)}`);
        return;
      }
      // Ordena por price_created descending — produtos novos no topo, leftovers
      // antigos no rodapé (mesmo se ainda não importados). Stripe já retorna
      // nessa ordem por default, mas explicito pra blindar de futura mudança
      // de API.
      const sorted = [...r.body.data].sort(
        (a, b) => b.price_created - a.price_created
      );
      setPrices(sorted);
      const initial: typeof drafts = {};
      for (const p of sorted) {
        if (p.already_imported) continue;
        initial[p.price_id] = {
          feature_key: slugifyFeatureKey(p.name),
          checkout_mode: p.mode === "subscription" ? "hosted" : "inline",
          // Usa stripe_product.metadata.brand como sugestão (já validado
          // contra service_clients no backend; se inválido vem null).
          default_brand: p.stripe_brand_hint ?? "",
        };
      }
      setDrafts(initial);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleImport = async (p: StripePriceForImport) => {
    const d = drafts[p.price_id];
    if (!d) return;
    if (!/^[a-z0-9_]+$/.test(d.feature_key)) {
      alert("feature_key inválido: use só [a-z0-9_]");
      return;
    }
    setSubmitting(p.price_id);
    const body: Record<string, unknown> = {
      feature_key: d.feature_key,
      name: p.name,
      description: p.description || p.name,
      mode: p.mode,
      amount_cents: p.amount_cents,
      currency: p.currency,
      checkout_mode: d.checkout_mode,
      stripe_product_id: p.product_id,
      stripe_price_id: p.price_id,
    };
    if (p.mode === "subscription" && p.recurring_interval) {
      body.recurring_interval = p.recurring_interval;
    }
    if (p.lookup_key) {
      body.stripe_lookup_key = p.lookup_key;
    }
    if (d.default_brand) {
      body.default_brand = d.default_brand;
    }
    const r = await api<FeatureCatalogRow>("/api/admin/feature-catalog", {
      method: "POST",
      body: JSON.stringify(body),
    });
    setSubmitting(null);
    if (r.ok) {
      onImported();
      refresh();
    } else {
      alert(`Erro (HTTP ${r.status}): ${JSON.stringify(r.body)}`);
    }
  };

  const available = prices.filter((p) => !p.already_imported).length;

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            Importar do Stripe{" "}
            <span className="muted" style={{ fontWeight: "normal" }}>
              ({available} disponíveis / {prices.length} no total)
            </span>
          </h3>
          <button
            className="modal-close"
            onClick={onClose}
            aria-label="Fechar"
          >
            ×
          </button>
        </div>
        <div className="modal-body">

      {loading && <p className="muted">Carregando do Stripe…</p>}
      {error && (
        <p className="error" style={{ color: "var(--danger, #c00)" }}>
          {error}
        </p>
      )}

      {!loading && !error && prices.length === 0 && (
        <p className="empty">Nenhum Price ativo no Stripe.</p>
      )}

      {!loading && prices.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Produto Stripe</th>
              <th>Preço</th>
              <th>Modo</th>
              <th>feature_key</th>
              <th>Checkout</th>
              <th>Brand</th>
              <th>Status</th>
              <th>Ação</th>
            </tr>
          </thead>
          <tbody>
            {prices.map((p) => {
              const d = drafts[p.price_id];
              const isSubmitting = submitting === p.price_id;
              return (
                <tr key={p.price_id}>
                  <td>
                    <div>{p.name}</div>
                    <div
                      className="muted"
                      style={{ fontSize: "0.8em", fontFamily: "monospace" }}
                    >
                      {p.product_id}
                    </div>
                  </td>
                  <td>
                    {brl(p.amount_cents)}
                    {p.mode === "subscription" && p.recurring_interval
                      ? ` / ${p.recurring_interval}`
                      : ""}
                  </td>
                  <td>
                    <code>{p.mode}</code>
                    {p.lookup_key && (
                      <div
                        className="muted"
                        style={{ fontSize: "0.8em" }}
                        title="lookup_key na Stripe"
                      >
                        lk: {p.lookup_key}
                      </div>
                    )}
                  </td>
                  <td>
                    {p.already_imported ? (
                      <code>{p.imported_feature_key}</code>
                    ) : (
                      <input
                        value={d?.feature_key ?? ""}
                        onChange={(e) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [p.price_id]: {
                              ...(prev[p.price_id] ?? {
                                checkout_mode:
                                  p.mode === "subscription"
                                    ? "hosted"
                                    : "inline",
                                default_brand: "",
                              }),
                              feature_key: e.target.value,
                            },
                          }))
                        }
                        disabled={isSubmitting}
                        style={{ width: "180px" }}
                      />
                    )}
                  </td>
                  <td>
                    {p.already_imported ? null : (
                      <select
                        value={d?.checkout_mode ?? "inline"}
                        onChange={(e) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [p.price_id]: {
                              ...(prev[p.price_id] ?? {
                                feature_key: slugifyFeatureKey(p.name),
                                default_brand: "",
                              }),
                              checkout_mode: e.target.value as
                                | "inline"
                                | "hosted",
                            },
                          }))
                        }
                        disabled={isSubmitting}
                      >
                        <option value="inline">inline</option>
                        <option value="hosted">hosted</option>
                      </select>
                    )}
                  </td>
                  <td>
                    {p.already_imported ? null : (
                      <select
                        value={d?.default_brand ?? ""}
                        onChange={(e) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [p.price_id]: {
                              ...(prev[p.price_id] ?? {
                                feature_key: slugifyFeatureKey(p.name),
                                checkout_mode:
                                  p.mode === "subscription"
                                    ? "hosted"
                                    : "inline",
                              }),
                              default_brand: e.target.value,
                            },
                          }))
                        }
                        disabled={isSubmitting}
                      >
                        <option value="">padrão icl</option>
                        {brands.map((b) => (
                          <option key={b.slug} value={b.slug}>
                            {b.slug}
                          </option>
                        ))}
                      </select>
                    )}
                    {!p.already_imported && p.stripe_brand_hint && (
                      <div
                        className="muted"
                        style={{ fontSize: "0.75em", marginTop: 2 }}
                        title="Lido de stripe_product.metadata.brand"
                      >
                        do Stripe: {p.stripe_brand_hint}
                      </div>
                    )}
                    {!p.already_imported &&
                      p.stripe_brand_raw &&
                      !p.stripe_brand_hint && (
                        <div
                          className="muted"
                          style={{
                            fontSize: "0.75em",
                            marginTop: 2,
                            color: "var(--danger, #c00)",
                          }}
                          title="metadata.brand não bate com nenhum service_client ativo"
                        >
                          Stripe diz "{p.stripe_brand_raw}" (inválido)
                        </div>
                      )}
                  </td>
                  <td>
                    <span
                      className={`pill pill-${p.already_imported ? "active" : "pending"}`}
                    >
                      {p.already_imported ? "Importado" : "Disponível"}
                    </span>
                  </td>
                  <td>
                    {p.already_imported ? (
                      <em className="muted">Já importado</em>
                    ) : (
                      <button
                        className="primary-btn"
                        onClick={() => handleImport(p)}
                        disabled={isSubmitting || !d?.feature_key}
                      >
                        {isSubmitting ? "Importando…" : "Importar"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
        </div>
        <div className="modal-footer">
          <button
            className="ghost-btn"
            onClick={refresh}
            disabled={loading}
          >
            Atualizar lista
          </button>
          <button className="ghost-btn" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SERVICE CLIENTS — apps que integram com a central (g4members, papo-legal)
// ============================================================================

type ServiceClient = {
  id: number;
  slug: string;
  name: string;
  webhookUrl: string | null;
  active: boolean;
  metadata: Record<string, string> | null;
  allowedRedirectOrigins: string[] | null;
  receiveAllEvents: boolean;
  forwardRaw: boolean;
  forwardRawEventTypes: string[] | null;
  deliveryDialect: "native" | "g4members";
  createdAt: string;
  updatedAt: string;
};

function ServiceClientsTab() {
  const [rows, setRows] = useState<ServiceClient[]>([]);
  const [revealedSecret, setRevealedSecret] = useState<
    { slug: string; secret: string } | null
  >(null);
  // Cadastro/edicao acontecem numa pagina dedicada (nao mais inline), reusando
  // o padrao do ProductDetailView. view = lista | novo | editar um existente.
  const [view, setView] = useState<
    { mode: "list" } | { mode: "new" } | { mode: "edit"; client: ServiceClient }
  >({ mode: "list" });

  const refresh = useCallback(() => {
    api<{ data: ServiceClient[] }>("/api/admin/service-clients").then(
      (r) => r.ok && setRows(r.body.data)
    );
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleRotate = async (id: number, slug: string) => {
    if (!confirm(`Rotacionar secret de "${slug}"? O secret atual deixa de funcionar imediatamente.`)) return;
    const r = await api<ServiceClient & { secret: string }>(
      `/api/admin/service-clients/${id}/rotate-secret`,
      { method: "POST" }
    );
    if (r.ok) {
      setRevealedSecret({ slug: r.body.slug, secret: r.body.secret });
      refresh();
    }
  };

  const handleToggleActive = async (c: ServiceClient) => {
    await api(`/api/admin/service-clients/${c.id}`, {
      method: "PATCH",
      body: JSON.stringify({ active: !c.active }),
    });
    refresh();
  };

  const handleToggleForwardRaw = async (c: ServiceClient) => {
    await api(`/api/admin/service-clients/${c.id}`, {
      method: "PATCH",
      body: JSON.stringify({ forwardRaw: !c.forwardRaw }),
    });
    refresh();
  };

  if (view.mode !== "list") {
    return (
      <ServiceClientDetailView
        client={view.mode === "edit" ? view.client : null}
        onBack={() => setView({ mode: "list" })}
        onSaved={(revealed) => {
          if (revealed) setRevealedSecret(revealed);
          setView({ mode: "list" });
          refresh();
        }}
      />
    );
  }

  return (
    <>
      {revealedSecret && (
        <div className="secret-reveal">
          <strong>Secret de "{revealedSecret.slug}" criado/rotacionado.</strong>
          <p className="hint">
            Esta é a <strong>única</strong> vez que ele aparece. Copie agora — depois só rotacionando.
          </p>
          <code className="secret-value">{revealedSecret.secret}</code>
          <div>
            <button
              className="ghost-btn"
              onClick={() => navigator.clipboard.writeText(revealedSecret.secret)}
            >
              Copiar
            </button>
            <button
              className="ghost-btn"
              onClick={() => setRevealedSecret(null)}
            >
              Já copiei
            </button>
          </div>
        </div>
      )}

      <div className="tab-toolbar">
        <button className="primary-btn" onClick={() => setView({ mode: "new" })}>
          + Novo client
        </button>
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Slug</th>
            <th>Nome</th>
            <th>URL do Webhook</th>
            <th>Ativo</th>
            <th>Criado</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={7} className="empty">Sem clientes externos.</td></tr>
          ) : (
            rows.map((c) => (
              <tr key={c.id}>
                <td>{c.id}</td>
                <td>
                  <code>{c.slug}</code>
                  {c.receiveAllEvents && (
                    <span className="chip chip-promo" title="receive_all_events=true">monitor</span>
                  )}
                  {c.forwardRaw && (
                    <span
                      className="chip chip-promo"
                      title={
                        c.forwardRawEventTypes && c.forwardRawEventTypes.length > 0
                          ? `forward_raw — tipos: ${c.forwardRawEventTypes.join(", ")}`
                          : "forward_raw — todos os tipos da marca"
                      }
                    >
                      cru
                    </span>
                  )}
                  {c.deliveryDialect === "g4members" && (
                    <span
                      className="chip chip-promo"
                      title="delivery_dialect=g4members — entrega no formato evento-custom (hottok) que o tema WordPress expõe"
                    >
                      g4members
                    </span>
                  )}
                </td>
                <td>{c.name}</td>
                <td>
                  {c.webhookUrl ? (
                    <code className="muted">{c.webhookUrl}</code>
                  ) : (
                    <em>—</em>
                  )}
                </td>
                <td><Pill status={c.active ? "active" : "inactive"} /></td>
                <td>{formatDate(c.createdAt)}</td>
                <td>
                  <RowActions>
                    <button
                      className="ghost-btn"
                      onClick={() => setView({ mode: "edit", client: c })}
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleRotate(c.id, c.slug)}
                      title="Gera um secret novo; o atual para de funcionar na hora"
                    >
                      Rotacionar secret
                    </button>
                    <button onClick={() => handleToggleForwardRaw(c)}>
                      {c.forwardRaw ? "Desativar webhook cru" : "Ativar webhook cru"}
                    </button>
                    <button onClick={() => handleToggleActive(c)}>
                      {c.active ? "Desativar client" : "Ativar client"}
                    </button>
                  </RowActions>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </>
  );
}

// Pagina dedicada de cadastro/edicao de cliente externo (reusa o padrao do
// ProductDetailView). client=null -> criar; client!=null -> editar. O slug e'
// imutavel (chave); pra trocar o secret/hottok usa-se o Rotate na lista.
function ServiceClientDetailView({
  client,
  onBack,
  onSaved,
}: {
  client: ServiceClient | null;
  onBack: () => void;
  onSaved: (revealed?: { slug: string; secret: string }) => void;
}) {
  const isEdit = client !== null;
  const [slug, setSlug] = useState(client?.slug ?? "");
  const [name, setName] = useState(client?.name ?? "");
  const [webhookUrl, setWebhookUrl] = useState(client?.webhookUrl ?? "");
  const [allowedRedirectOrigins, setAllowedRedirectOrigins] = useState(
    (client?.allowedRedirectOrigins ?? []).join("\n")
  );
  const [receiveAllEvents, setReceiveAllEvents] = useState(
    client?.receiveAllEvents ?? false
  );
  const [forwardRaw, setForwardRaw] = useState(client?.forwardRaw ?? false);
  const [forwardRawEventTypes, setForwardRawEventTypes] = useState(
    (client?.forwardRawEventTypes ?? []).join("\n")
  );
  const [deliveryDialect, setDeliveryDialect] = useState<"native" | "g4members">(
    client?.deliveryDialect ?? "native"
  );
  const [hottok, setHottok] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const origins = allowedRedirectOrigins
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const rawTypes = forwardRawEventTypes
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    setSubmitting(true);
    if (isEdit) {
      // Slug e secret nao vao no PATCH (slug imutavel; secret via rotate).
      const r = await api<ServiceClient>(
        `/api/admin/service-clients/${client.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            name,
            webhookUrl: webhookUrl.trim() === "" ? null : webhookUrl.trim(),
            allowedRedirectOrigins: origins.length > 0 ? origins : null,
            receiveAllEvents,
            forwardRaw,
            forwardRawEventTypes: forwardRaw
              ? rawTypes.length > 0
                ? rawTypes
                : null
              : null,
            deliveryDialect,
          }),
        }
      );
      setSubmitting(false);
      if (r.ok) onSaved();
      else setError(`Erro (HTTP ${r.status}): ${JSON.stringify(r.body)}`);
    } else {
      const r = await api<ServiceClient & { secret: string }>(
        "/api/admin/service-clients",
        {
          method: "POST",
          body: JSON.stringify({
            slug,
            name,
            webhookUrl: webhookUrl || undefined,
            allowedRedirectOrigins: origins.length > 0 ? origins : undefined,
            receiveAllEvents,
            forwardRaw,
            // Vazio = todos os tipos da marca.
            forwardRawEventTypes: rawTypes.length > 0 ? rawTypes : undefined,
            deliveryDialect,
            // g4members: o `secret` É o hottok (credencial que o tema valida);
            // native: o backend gera o HMAC key.
            secret: deliveryDialect === "g4members" ? hottok : undefined,
          }),
        }
      );
      setSubmitting(false);
      if (r.ok) onSaved({ slug: r.body.slug, secret: r.body.secret });
      else setError(`Erro (HTTP ${r.status}): ${JSON.stringify(r.body)}`);
    }
  };

  return (
    <div className="product-detail">
      <div className="tab-toolbar">
        <button className="ghost-btn" onClick={onBack}>
          ← Clientes
        </button>
      </div>

      <header className="page-header" style={{ marginTop: 8 }}>
        <h2 style={{ margin: 0 }}>
          {isEdit ? `Editar cliente · ${client.slug}` : "Novo cliente externo"}
        </h2>
        <p className="hint">
          {isEdit
            ? "Slug é imutável. Para trocar o secret/hottok, use Rotate na lista."
            : "Consumidor externo dos webhooks (papo-legal, g4members, ...)."}
        </p>
      </header>

      <form className="inline-form" onSubmit={submit}>
        <label>
          Slug (kebab-case)
          <input
            required
            pattern="^[a-z0-9][a-z0-9-]*$"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="papo-legal"
            disabled={submitting || isEdit}
            autoFocus={!isEdit}
          />
        </label>
        <label>
          Nome
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="PapoLegal"
            disabled={submitting}
          />
        </label>
        <label>
          Webhook URL (opcional)
          <input
            type="url"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder={
              deliveryDialect === "g4members"
                ? "https://membro.icl.com.br/wp-json/hothook/v2/users"
                : "https://papo-legal.com/wp-json/strihook/v1/users"
            }
            disabled={submitting}
          />
        </label>
        <label>
          Dialeto de entrega
          <select
            value={deliveryDialect}
            onChange={(e) =>
              setDeliveryDialect(e.target.value as "native" | "g4members")
            }
            disabled={submitting}
          >
            <option value="native">native (feature.* + HMAC)</option>
            <option value="g4members">g4members (evento-custom + hottok)</option>
          </select>
        </label>
        {deliveryDialect === "g4members" && !isEdit && (
          <label>
            Hottok (credencial que o g4members valida)
            <input
              required
              value={hottok}
              onChange={(e) => setHottok(e.target.value)}
              placeholder="token do tema WordPress"
              disabled={submitting}
            />
          </label>
        )}
        {deliveryDialect === "g4members" && isEdit && (
          <p className="hint full-width" style={{ gridColumn: "1 / -1" }}>
            Para alterar o hottok, use o botão Rotate na lista.
          </p>
        )}
        <label className="full-width">
          Allowed redirect origins (opcional, 1 por linha — origens válidas pra success_url/cancel_url no /api/v1/checkout-sessions)
          <textarea
            rows={3}
            value={allowedRedirectOrigins}
            onChange={(e) => setAllowedRedirectOrigins(e.target.value)}
            placeholder={`https://papo-legal.com\nhttps://app.papo-legal.com`}
            disabled={submitting}
          />
        </label>
        <label className="full-width checkbox-label">
          <input
            type="checkbox"
            checked={receiveAllEvents}
            onChange={(e) => setReceiveAllEvents(e.target.checked)}
            disabled={submitting}
          />
          <span>
            <strong>Monitor</strong> — receive_all_events: este client recebe TODOS os outbound webhooks, independente do brand
          </span>
        </label>
        <label className="full-width checkbox-label">
          <input
            type="checkbox"
            checked={forwardRaw}
            onChange={(e) => setForwardRaw(e.target.checked)}
            disabled={submitting}
          />
          <span>
            <strong>Webhook cru</strong> — forward_raw: além do broadcast processado (feature.*), recebe o evento Stripe verbatim (header X-ICL-Raw), roteado pelo brand do client
          </span>
        </label>
        {forwardRaw && (
          <label className="full-width">
            Tipos de evento cru (opcional, 1 por linha — vazio = todos os tipos da marca)
            <textarea
              rows={3}
              value={forwardRawEventTypes}
              onChange={(e) => setForwardRawEventTypes(e.target.value)}
              placeholder={`invoice.payment_failed\ncharge.dispute.created\ncustomer.subscription.deleted`}
              disabled={submitting}
            />
          </label>
        )}
        {error && (
          <p className="error" style={{ gridColumn: "1 / -1" }}>
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="submit" className="primary-btn" disabled={submitting}>
            {submitting
              ? isEdit
                ? "Salvando..."
                : "Criando..."
              : isEdit
                ? "Salvar"
                : "Criar"}
          </button>
          <button
            type="button"
            className="ghost-btn"
            onClick={onBack}
            disabled={submitting}
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

// ============================================================================
// OFFERS — catalogo de ofertas/campanhas (espelho Hotmart)
// ============================================================================

type OfferRow = {
  id: number;
  code: string;
  featureKey: string;
  productName: string | null;
  name: string;
  stripePriceId: string | null;
  promotionCodeId: string | null;
  brand: string | null;
  campaignId: string | null;
  landingUrl: string | null;
  startsAt: string | null;
  endsAt: string | null;
  active: boolean;
  archivedAt: string | null;
  createdAt: string;
};
type OfferProduct = { featureKey: string; name: string; mode: string };

const OFFERS_PAGE = 50;

function OffersTab({ initialFeatureKey = "" }: { initialFeatureKey?: string }) {
  const [rows, setRows] = useState<OfferRow[]>([]);
  const [total, setTotal] = useState(0);
  const [products, setProducts] = useState<OfferProduct[]>([]);
  const [q, setQ] = useState("");
  // Pre-filtra pelo produto quando aberto via "Ofertas" do Catalogo.
  const [featureKey, setFeatureKey] = useState(initialFeatureKey);
  const [activeFilter, setActiveFilter] = useState<"" | "true" | "false">("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [offset, setOffset] = useState(0);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<OfferRow | null>(null);
  const [stats, setStats] = useState<
    Record<string, { count: number; brlCents: number }>
  >({});

  const refresh = useCallback(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (featureKey) p.set("feature_key", featureKey);
    if (activeFilter) p.set("active", activeFilter);
    if (includeArchived) p.set("include_archived", "true");
    p.set("limit", String(OFFERS_PAGE));
    p.set("offset", String(offset));
    api<{ data: OfferRow[]; total: number }>(`/api/admin/offers?${p}`).then((r) => {
      if (r.ok) {
        setRows(r.body.data);
        setTotal(r.body.total);
      }
    });
  }, [q, featureKey, activeFilter, includeArchived, offset]);

  useEffect(() => {
    refresh();
  }, [refresh]);
  useEffect(() => {
    api<{ data: OfferProduct[] }>("/api/admin/feature-catalog").then(
      (r) => r.ok && setProducts(r.body.data)
    );
    // Stats de atribuicao por oferta (vendas + receita BRL), em bulk.
    api<{
      data: Array<{ offerCode: string; currency: string; count: number; sumCents: number }>;
    }>("/api/admin/offers/stats").then((r) => {
      if (!r.ok) return;
      const m: Record<string, { count: number; brlCents: number }> = {};
      for (const s of r.body.data) {
        const e = m[s.offerCode] ?? { count: 0, brlCents: 0 };
        e.count += s.count;
        if ((s.currency ?? "").toUpperCase() === "BRL") e.brlCents += s.sumCents;
        m[s.offerCode] = e;
      }
      setStats(m);
    });
  }, []);

  const submitOffer = async (form: Record<string, unknown>) => {
    const r = editing
      ? await api(`/api/admin/offers/${editing.id}`, {
          method: "PATCH",
          body: JSON.stringify(form),
        })
      : await api("/api/admin/offers", {
          method: "POST",
          body: JSON.stringify(form),
        });
    if (r.ok) {
      setCreating(false);
      setEditing(null);
      refresh();
    } else {
      alert(`Erro (HTTP ${r.status}): ${JSON.stringify(r.body)}`);
    }
  };

  const handleArchive = async (o: OfferRow) => {
    if (!confirm(`Arquivar a oferta "${o.code}"? Ela para de funcionar nos links.`))
      return;
    await api(`/api/admin/offers/${o.id}/archive`, { method: "POST" });
    refresh();
  };
  const handleToggleActive = async (o: OfferRow) => {
    await api(`/api/admin/offers/${o.id}`, {
      method: "PATCH",
      body: JSON.stringify({ active: !o.active }),
    });
    refresh();
  };
  const copyLink = (code: string) => {
    navigator.clipboard.writeText(
      `${window.location.origin}/?offer=${encodeURIComponent(code)}`
    );
  };

  const pages = Math.max(1, Math.ceil(total / OFFERS_PAGE));
  const page = Math.floor(offset / OFFERS_PAGE) + 1;

  return (
    <>
      <div className="tab-toolbar" style={{ gap: 8, flexWrap: "wrap" }}>
        <input
          placeholder="Buscar code/nome"
          value={q}
          onChange={(e) => {
            setOffset(0);
            setQ(e.target.value);
          }}
        />
        <select
          value={featureKey}
          onChange={(e) => {
            setOffset(0);
            setFeatureKey(e.target.value);
          }}
        >
          <option value="">Todos os produtos</option>
          {products.map((p) => (
            <option key={p.featureKey} value={p.featureKey}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          value={activeFilter}
          onChange={(e) => {
            setOffset(0);
            setActiveFilter(e.target.value as "" | "true" | "false");
          }}
        >
          <option value="">Ativo + inativo</option>
          <option value="true">Só ativos</option>
          <option value="false">Só inativos</option>
        </select>
        <label className="checkbox-label" style={{ display: "inline-flex", gap: 4 }}>
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => {
              setOffset(0);
              setIncludeArchived(e.target.checked);
            }}
          />
          <span>incluir arquivadas</span>
        </label>
        {!creating && !editing && (
          <button className="primary-btn" onClick={() => setCreating(true)}>
            + Nova oferta
          </button>
        )}
      </div>

      {(creating || editing) && (
        <OfferForm
          products={products}
          editing={editing}
          onCancel={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSubmit={submitOffer}
        />
      )}

      <table className="data-table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Produto</th>
            <th>Nome</th>
            <th>Preço</th>
            <th>Cupom</th>
            <th>Campanha</th>
            <th>Vendas</th>
            <th>Receita</th>
            <th>Status</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={10} className="empty">
                Nenhuma oferta.
              </td>
            </tr>
          ) : (
            rows.map((o) => (
              <tr key={o.id}>
                <td>
                  <code>{o.code}</code>
                </td>
                <td>{o.productName ?? o.featureKey}</td>
                <td>{o.name}</td>
                <td>
                  {o.stripePriceId ? (
                    <code className="muted">{o.stripePriceId}</code>
                  ) : (
                    <em>catálogo</em>
                  )}
                </td>
                <td>
                  {o.promotionCodeId ? (
                    <span className="chip chip-promo">sim</span>
                  ) : (
                    <em>—</em>
                  )}
                </td>
                <td>{o.campaignId ?? <em>—</em>}</td>
                <td>{stats[o.code]?.count ?? 0}</td>
                <td>
                  {stats[o.code] ? (
                    `R$ ${(stats[o.code].brlCents / 100).toFixed(2)}`
                  ) : (
                    <em>—</em>
                  )}
                </td>
                <td>
                  {o.archivedAt ? (
                    <span className="chip">arquivada</span>
                  ) : (
                    <Pill status={o.active ? "active" : "inactive"} />
                  )}
                </td>
                <td>
                  <button className="ghost-btn" onClick={() => copyLink(o.code)}>
                    Link
                  </button>
                  {!o.archivedAt && (
                    <>
                      <button
                        className="ghost-btn"
                        onClick={() => {
                          setCreating(false);
                          setEditing(o);
                        }}
                      >
                        Editar
                      </button>
                      <button
                        className="ghost-btn"
                        onClick={() => handleToggleActive(o)}
                      >
                        {o.active ? "Desativar" : "Ativar"}
                      </button>
                      <button className="ghost-btn" onClick={() => handleArchive(o)}>
                        Arquivar
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className="tab-toolbar">
        <button
          className="ghost-btn"
          disabled={offset === 0}
          onClick={() => setOffset(Math.max(0, offset - OFFERS_PAGE))}
        >
          ‹ Anterior
        </button>
        <span>
          Página {page} de {pages} ({total} ofertas)
        </span>
        <button
          className="ghost-btn"
          disabled={page >= pages}
          onClick={() => setOffset(offset + OFFERS_PAGE)}
        >
          Próxima ›
        </button>
      </div>
    </>
  );
}

function OfferForm({
  products,
  editing,
  lockedFeatureKey,
  onCancel,
  onSubmit,
}: {
  products: OfferProduct[];
  editing: OfferRow | null;
  // Quando setado (painel por-produto), o produto vem fixo e o select sai.
  lockedFeatureKey?: string;
  onCancel: () => void;
  onSubmit: (form: Record<string, unknown>) => Promise<void>;
}) {
  const [code, setCode] = useState(editing?.code ?? "");
  const [featureKey, setFeatureKey] = useState(
    editing?.featureKey ?? lockedFeatureKey ?? ""
  );
  const [name, setName] = useState(editing?.name ?? "");
  const [stripePriceId, setStripePriceId] = useState(editing?.stripePriceId ?? "");
  const [promotionCodeId, setPromotionCodeId] = useState(
    editing?.promotionCodeId ?? ""
  );
  const [brand, setBrand] = useState(editing?.brand ?? "");
  const [campaignId, setCampaignId] = useState(editing?.campaignId ?? "");
  const [landingUrl, setLandingUrl] = useState(editing?.landingUrl ?? "");
  const [submitting, setSubmitting] = useState(false);

  return (
    <form
      className="inline-form"
      onSubmit={async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const payload: Record<string, unknown> = {
          featureKey,
          name,
          stripePriceId: stripePriceId || null,
          promotionCodeId: promotionCodeId || null,
          brand: brand || null,
          campaignId: campaignId || null,
          landingUrl: landingUrl || null,
        };
        if (!editing) payload.code = code; // code e' imutavel
        await onSubmit(payload);
        setSubmitting(false);
      }}
    >
      <label>
        Code (kebab-case){editing ? " — imutável" : ""}
        <input
          required
          pattern="^[a-z0-9][a-z0-9-]*$"
          value={code}
          disabled={!!editing || submitting}
          onChange={(e) => setCode(e.target.value)}
          placeholder="icl-w45-anual"
          autoFocus={!editing}
        />
      </label>
      {lockedFeatureKey ? (
        <label>
          Produto
          <input value={lockedFeatureKey} disabled readOnly />
        </label>
      ) : (
        <label>
          Produto
          <select
            required
            value={featureKey}
            disabled={submitting}
            onChange={(e) => setFeatureKey(e.target.value)}
          >
            <option value="">Selecione...</option>
            {products.map((p) => (
              <option key={p.featureKey} value={p.featureKey}>
                {p.name} ({p.mode})
              </option>
            ))}
          </select>
        </label>
      )}
      <label>
        Nome (rótulo admin)
        <input
          required
          value={name}
          disabled={submitting}
          onChange={(e) => setName(e.target.value)}
          placeholder="W45 - Anual"
        />
      </label>
      <label>
        Stripe Price ID (opcional — vazio = preço ativo do catálogo)
        <input
          value={stripePriceId}
          disabled={submitting}
          onChange={(e) => setStripePriceId(e.target.value)}
          placeholder="price_..."
        />
      </label>
      <label>
        Promotion Code ID (cupom auto, opcional)
        <input
          value={promotionCodeId}
          disabled={submitting}
          onChange={(e) => setPromotionCodeId(e.target.value)}
          placeholder="promo_..."
        />
      </label>
      <label>
        Brand (opcional — senão usa o default do produto)
        <input
          value={brand}
          disabled={submitting}
          onChange={(e) => setBrand(e.target.value)}
          placeholder="papo-legal"
        />
      </label>
      <label>
        Campaign ID (atribuição, opcional)
        <input
          value={campaignId}
          disabled={submitting}
          onChange={(e) => setCampaignId(e.target.value)}
          placeholder="w45"
        />
      </label>
      <label className="full-width">
        Landing URL (opcional)
        <input
          type="url"
          value={landingUrl}
          disabled={submitting}
          onChange={(e) => setLandingUrl(e.target.value)}
          placeholder="https://..."
        />
      </label>
      <div className="form-actions">
        <button type="submit" className="primary-btn" disabled={submitting}>
          {submitting ? "Salvando..." : editing ? "Salvar" : "Criar"}
        </button>
        <button
          type="button"
          className="ghost-btn"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

// ============================================================================
// OUTBOUND WEBHOOKS — log de tentativas de entrega central → apps
// ============================================================================

type OutboundAttempt = {
  id: number;
  serviceClientId: number;
  clientSlug: string;
  eventId: string;
  eventType: string;
  targetUrl: string;
  attemptN: number;
  maxAttempts: number;
  status: "pending" | "in_flight" | "delivered" | "dead_letter";
  nextRetryAt: string;
  lastResponseStatus: number | null;
  lastError: string | null;
  deliveredAt: string | null;
  createdAt: string;
  updatedAt: string;
};

function OutboundTab() {
  const [rows, setRows] = useState<OutboundAttempt[]>([]);
  const [filter, setFilter] = useState<"all" | OutboundAttempt["status"]>("all");
  const [expanded, setExpanded] = useState<number | null>(null);
  const [detail, setDetail] = useState<any>(null);

  const refresh = useCallback(() => {
    const url =
      filter === "all"
        ? "/api/admin/outbound-webhooks"
        : `/api/admin/outbound-webhooks?status=${filter}`;
    api<{ data: OutboundAttempt[] }>(url).then(
      (r) => r.ok && setRows(r.body.data)
    );
  }, [filter]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 3000);
    return () => clearInterval(t);
  }, [refresh]);

  const toggle = async (id: number) => {
    if (expanded === id) {
      setExpanded(null);
      setDetail(null);
      return;
    }
    setExpanded(id);
    setDetail(null);
    const r = await api(`/api/admin/outbound-webhooks/${id}`);
    setDetail(r.body);
  };

  const handleRetry = async (id: number) => {
    await api(`/api/admin/outbound-webhooks/${id}/retry`, { method: "POST" });
    refresh();
  };

  return (
    <>
      <div className="tab-toolbar">
        <label>
          Filtro:&nbsp;
          <select value={filter} onChange={(e) => setFilter(e.target.value as any)}>
            <option value="all">Todos</option>
            <option value="pending">Pendentes</option>
            <option value="in_flight">Em entrega</option>
            <option value="delivered">Entregues</option>
            <option value="dead_letter">Dead-letter</option>
          </select>
        </label>
      </div>
      <table className="data-table">
        <thead>
          <tr>
            <th>Horário</th>
            <th>Cliente</th>
            <th>Evento</th>
            <th>Tentativas</th>
            <th>Status</th>
            <th>Resp</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={7} className="empty">Sem entregas.</td></tr>
          ) : (
            rows.map((a) => (
              <Fragment key={a.id}>
                <tr
                  onClick={() => toggle(a.id)}
                  className={`clickable ${expanded === a.id ? "row-expanded" : ""}`}
                >
                  <td>{formatDate(a.createdAt)}</td>
                  <td><code>{a.clientSlug}</code></td>
                  <td><code>{a.eventType}</code></td>
                  <td>{a.attemptN}/{a.maxAttempts === 999 ? "∞" : a.maxAttempts}</td>
                  <td><Pill status={a.status} /></td>
                  <td>
                    {a.lastResponseStatus ?? (
                      a.lastError ? <span className="muted">{a.lastError}</span> : "—"
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {a.status === "dead_letter" && (
                      <button className="ghost-btn" onClick={() => handleRetry(a.id)}>
                        Retry
                      </button>
                    )}
                  </td>
                </tr>
                {expanded === a.id && (
                  <tr className="row-detail">
                    <td colSpan={7}>
                      {detail === null ? (
                        <p>Carregando...</p>
                      ) : (
                        <>
                          <p className="muted">
                            Target: <code>{a.targetUrl}</code>
                          </p>
                          <p className="muted">
                            Event ID: <code>{a.eventId}</code>
                          </p>
                          <pre>{JSON.stringify(detail, null, 2)}</pre>
                        </>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))
          )}
        </tbody>
      </table>
    </>
  );
}

// ============================================================================
// CAMPAIGNS — agregação por campaign_id (count/sum/refunded/net)
// ============================================================================

type CampaignStat = {
  campaign_id: string;
  brand: string | null;
  total_count: number;
  succeeded_count: number;
  succeeded_amount_cents: number;
  refunded_amount_cents: number;
  net_amount_cents: number;
  first_seen_at: string;
  last_seen_at: string;
};

function CampaignsTab() {
  const [rows, setRows] = useState<CampaignStat[]>([]);
  const refresh = useCallback(() => {
    api<{ data: CampaignStat[] }>("/api/admin/campaigns/stats").then(
      (r) => r.ok && setRows(r.body.data)
    );
  }, []);
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [refresh]);

  const totals = rows.reduce(
    (acc, r) => {
      acc.succeeded += r.succeeded_amount_cents;
      acc.refunded += r.refunded_amount_cents;
      acc.net += r.net_amount_cents;
      return acc;
    },
    { succeeded: 0, refunded: 0, net: 0 }
  );

  return (
    <>
      <div className="tab-toolbar">
        <span className="muted result-count">
          {rows.length} campanha(s) · bruto {brl(totals.succeeded)} · reembolsado{" "}
          {brl(totals.refunded)} · líquido <strong>{brl(totals.net)}</strong>
        </span>
      </div>
      <table className="data-table">
        <thead>
          <tr>
            <th>Campanha</th>
            <th>Brand</th>
            <th>Total / Pagas</th>
            <th>Bruto</th>
            <th>Reembolsado</th>
            <th>Líquido</th>
            <th>Primeira</th>
            <th>Última</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={8} className="empty">Sem campanhas registradas.</td></tr>
          ) : (
            rows.map((r) => (
              <tr key={r.campaign_id}>
                <td><code>{r.campaign_id}</code></td>
                <td>
                  {r.brand ? (
                    <span className="chip chip-brand">{r.brand}</span>
                  ) : <em>—</em>}
                </td>
                <td>{r.total_count} / <strong>{r.succeeded_count}</strong></td>
                <td>{brl(r.succeeded_amount_cents)}</td>
                <td>
                  {r.refunded_amount_cents > 0
                    ? <span className="muted">−{brl(r.refunded_amount_cents)}</span>
                    : <span className="muted">—</span>}
                </td>
                <td><strong>{brl(r.net_amount_cents)}</strong></td>
                <td>{formatDate(r.first_seen_at)}</td>
                <td>{formatDate(r.last_seen_at)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────── Radar

function RiskBadge({ level }: { level: string | null }) {
  if (!level) return <span className="muted">—</span>;
  // normal=verde, elevated=amarelo, highest=vermelho. Reusa as pills existentes
  // mapeando pra status conhecidos de cor.
  const cls =
    level === "highest"
      ? "pill pill-failed"
      : level === "elevated"
        ? "pill pill-pending"
        : level === "normal"
          ? "pill pill-succeeded"
          : "pill";
  return <span className={cls}>{level}</span>;
}

function RadarTab() {
  const [rows, setRows] = useState<RadarRow[]>([]);
  const [riskLevel, setRiskLevel] = useState("");
  const [reviewStatus, setReviewStatus] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(() => {
    const qs = new URLSearchParams();
    if (riskLevel) qs.set("risk_level", riskLevel);
    if (reviewStatus) qs.set("review_status", reviewStatus);
    api<{ data: RadarRow[] }>(`/api/admin/radar?${qs.toString()}`).then(
      (r) => r.ok && setRows(r.body.data)
    );
  }, [riskLevel, reviewStatus]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [refresh]);

  const approve = async (reviewId: string) => {
    if (
      !confirm(
        "Aprovar esta compra de alto risco? A review será fechada como aprovada."
      )
    )
      return;
    setBusy(reviewId);
    const r = await api(`/api/admin/radar/reviews/${reviewId}/approve`, {
      method: "POST",
    });
    setBusy(null);
    if (!r.ok)
      alert(`Falha ao aprovar (HTTP ${r.status}): ${JSON.stringify(r.body)}`);
    refresh();
  };

  const refundFraud = async (reviewId: string) => {
    if (
      !confirm(
        "Recusar e reembolsar como FRAUDE? O valor será estornado e a review fechada como refunded_as_fraud."
      )
    )
      return;
    setBusy(reviewId);
    const r = await api(
      `/api/admin/radar/reviews/${reviewId}/refund-as-fraud`,
      { method: "POST" }
    );
    setBusy(null);
    if (!r.ok)
      alert(
        `Falha no refund-as-fraud (HTTP ${r.status}): ${JSON.stringify(r.body)}`
      );
    refresh();
  };

  return (
    <>
      <div className="tab-toolbar">
        <select value={riskLevel} onChange={(e) => setRiskLevel(e.target.value)}>
          <option value="">Todos os riscos</option>
          <option value="normal">normal</option>
          <option value="elevated">elevated</option>
          <option value="highest">highest</option>
        </select>
        <select
          value={reviewStatus}
          onChange={(e) => setReviewStatus(e.target.value)}
        >
          <option value="">Review: qualquer</option>
          <option value="open">em revisão (open)</option>
          <option value="closed">revisão fechada</option>
        </select>
      </div>
      <table className="data-table">
        <thead>
          <tr>
            <th>Risco</th>
            <th>Score</th>
            <th>Outcome</th>
            <th>Review</th>
            <th>Regra</th>
            <th>Usuário</th>
            <th>Valor</th>
            <th>Charge</th>
            <th>Quando</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={10} className="empty">
                Sem outcomes do Radar. Compre na loja com um cartão de teste.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr key={r.id}>
                <td><RiskBadge level={r.riskLevel} /></td>
                <td>{r.riskScore != null ? r.riskScore : <span className="muted">—</span>}</td>
                <td><code className="muted">{r.outcomeType ?? "—"}</code></td>
                <td>
                  {r.reviewStatus ? (
                    <span title={r.reviewClosedReason ?? r.reviewOpenedReason ?? ""}>
                      <Pill status={r.reviewStatus === "open" ? "pending" : "succeeded"} />
                      {r.reviewClosedReason ? ` (${r.reviewClosedReason})` : ""}
                    </span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>
                  {r.ruleId ? <code className="muted">{r.ruleId}</code> : <span className="muted">—</span>}
                </td>
                <td>{r.userEmail ?? <span className="muted">—</span>}</td>
                <td>{r.amount != null ? brl(r.amount, r.currency ?? "brl") : "—"}</td>
                <td><code className="muted">{r.stripeChargeId}</code></td>
                <td>{formatDate(r.createdAt)}</td>
                <td>
                  {r.reviewStatus === "open" && r.reviewId ? (
                    <div className="addon-actions">
                      <button
                        className="ghost-btn small"
                        disabled={busy === r.reviewId}
                        onClick={() => approve(r.reviewId!)}
                      >
                        {busy === r.reviewId ? "..." : "Aprovar"}
                      </button>
                      <button
                        className="ghost-btn small"
                        disabled={busy === r.reviewId}
                        onClick={() => refundFraud(r.reviewId!)}
                      >
                        Reembolsar (fraude)
                      </button>
                    </div>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </>
  );
}

// ─────────────────────────────────────────────────────────────── Notas Fiscais

function FiscalTab() {
  const [rows, setRows] = useState<FiscalRow[]>([]);
  const [status, setStatus] = useState("");
  const [refreshing, setRefreshing] = useState<number | null>(null);

  const load = useCallback(() => {
    const qs = status ? `?status=${status}` : "";
    api<{ data: FiscalRow[] }>(`/api/admin/fiscal-invoices${qs}`).then(
      (r) => r.ok && setRows(r.body.data)
    );
  }, [status]);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    // Auto-refresh: a cada ciclo, pede pro backend reconsultar as notas pendentes
    // na NFe.io (converge pending -> issued sem clique). Em prod o webhook faz isso.
    const r = setInterval(() => {
      api("/api/admin/fiscal-invoices/refresh-pending", {
        method: "POST",
        body: JSON.stringify({}),
      }).then(() => load());
    }, 6000);
    return () => {
      clearInterval(t);
      clearInterval(r);
    };
  }, [load]);

  const refreshInvoice = async (id: number) => {
    setRefreshing(id);
    await api(`/api/admin/fiscal-invoices/${id}/refresh`, {
      method: "POST",
      body: JSON.stringify({}),
    });
    setRefreshing(null);
    load();
  };

  // Reemite uma nota em erro. Pergunta o CPF/CNPJ (caso mais comum: faltou/errou
  // na emissao); vazio reusa o que foi salvo.
  const retryInvoice = async (id: number) => {
    const tax = prompt(
      "Reemitir nota. CPF/CNPJ pra corrigir (deixe vazio pra reusar o salvo):",
      ""
    );
    if (tax === null) return;
    setRefreshing(id);
    const body = tax.trim() ? { tax_number: tax.trim() } : {};
    const r = await api(`/api/admin/fiscal-invoices/${id}/retry`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    setRefreshing(null);
    if (!r.ok) {
      alert(`Falha ao reemitir (HTTP ${r.status}): ${JSON.stringify(r.body)}`);
    } else if ((r.body as any)?.data?.status === "error") {
      alert(
        `Reemissao ainda em erro: ${(r.body as any)?.data?.errorMessage ?? ""}`
      );
    }
    load();
  };

  return (
    <>
      <div className="tab-toolbar">
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Todos os status</option>
          <option value="pending">pending</option>
          <option value="issued">issued</option>
          <option value="error">error</option>
          <option value="canceled">canceled</option>
        </select>
      </div>
      <table className="data-table">
        <thead>
          <tr>
            <th>Status</th>
            <th>Provedor</th>
            <th>Valor</th>
            <th>Usuário</th>
            <th>PI</th>
            <th>NF (provedor)</th>
            <th>PDF</th>
            <th>Erro</th>
            <th>Criada</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={10} className="empty">
                Nenhuma nota. Ative NF_PROVIDER e faça uma compra one-time.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr key={r.id}>
                <td><Pill status={r.status} /></td>
                <td><code className="muted">{r.provider}</code></td>
                <td>{brl(r.amount, r.currency)}</td>
                <td>{r.userEmail ?? <span className="muted">—</span>}</td>
                <td>
                  {r.stripePaymentIntentId ? (
                    <code className="muted">{r.stripePaymentIntentId}</code>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>
                  {r.providerInvoiceId ? (
                    <code className="muted">{r.providerInvoiceId}</code>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>
                  {r.pdfUrl ? (
                    <a href={r.pdfUrl} target="_blank" rel="noreferrer">
                      abrir
                    </a>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>
                  {r.errorMessage ? (
                    <span className="pill pill-failed" title={r.errorMessage}>
                      erro
                    </span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>{formatDate(r.createdAt)}</td>
                <td>
                  {r.status === "pending" || r.status === "error" ? (
                    <>
                      <button
                        className="ghost-btn"
                        onClick={() => refreshInvoice(r.id)}
                        disabled={refreshing === r.id}
                      >
                        {refreshing === r.id ? "..." : "Atualizar"}
                      </button>
                      {r.status === "error" && (
                        <button
                          className="ghost-btn"
                          onClick={() => retryInvoice(r.id)}
                          disabled={refreshing === r.id}
                          style={{ marginLeft: 4 }}
                        >
                          Reemitir
                        </button>
                      )}
                    </>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </>
  );
}

// ─────────────────────────────────────────────────────────── Connect / Afiliados

function ConnectTab() {
  const [accounts, setAccounts] = useState<ConnectAccount[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // acao em andamento por conta: `${acctId}:${acao}` (sync|onboarding)
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  // form de criação
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [pct, setPct] = useState("30");

  const [transfers, setTransfers] = useState<ConnectTransfer[]>([]);
  // Quando setado, a secao de repasses filtra so esse afiliado (+ KPIs dele).
  const [selectedAffiliate, setSelectedAffiliate] = useState<string | null>(null);
  // Connect nao habilitado na conta Stripe: mostra aviso e desabilita as acoes
  // em vez de deixar o erro cru da Stripe vazar. Detectado pelo /transfers (que
  // bate na Stripe); /accounts e' so DB e nao acusa a condicao.
  const [connectDisabled, setConnectDisabled] = useState(false);
  // O aviso e' fechavel no X (fica fixo enquanto o usuario nao dispensa). O
  // polling do /transfers segue atualizando connectDisabled, mas o banner so
  // some quando dispensado ou quando o Connect passa a funcionar.
  const [noticeDismissed, setNoticeDismissed] = useState(false);

  const refresh = useCallback(() => {
    api<{ data: ConnectAccount[] }>("/api/admin/connect/accounts").then(
      (r) => r.ok && setAccounts(r.body.data)
    );
    api<{ data: ConnectTransfer[] }>("/api/admin/connect/transfers").then((r) => {
      if (r.ok) {
        setTransfers(r.body.data);
        setConnectDisabled(false);
        setNoticeDismissed(false);
      } else if ((r.body as any)?.error === "connect_not_enabled") {
        setConnectDisabled(true);
      }
    });
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [refresh]);

  const createAccount = async () => {
    if (!email) {
      setMsg("Informe o email do afiliado.");
      return;
    }
    setBusy(true);
    setMsg(null);
    const r = await api("/api/admin/connect/accounts", {
      method: "POST",
      body: JSON.stringify({
        email,
        name: name || undefined,
        affiliateCode: code || undefined,
        commissionPct: Number(pct) || 30,
      }),
    });
    setBusy(false);
    if (r.ok) {
      setMsg("Conta criada. Gere o link de onboarding na linha abaixo.");
      setEmail("");
      setName("");
      setCode("");
      refresh();
    } else if ((r.body as any)?.error === "connect_not_enabled") {
      setConnectDisabled(true);
      setMsg((r.body as any).message);
    } else {
      setMsg(`Falha: ${(r.body as any)?.message ?? r.status}`);
    }
  };

  const onboardingLink = async (acctId: string) => {
    const origin = window.location.origin;
    setRowBusy(`${acctId}:onboarding`);
    const r = await api<{ url: string }>(
      `/api/admin/connect/accounts/${acctId}/onboarding-link`,
      {
        method: "POST",
        body: JSON.stringify({
          returnUrl: `${origin}/admin`,
          refreshUrl: `${origin}/admin`,
        }),
      }
    );
    setRowBusy(null);
    if (r.ok && r.body.url) {
      window.open(r.body.url, "_blank", "noreferrer");
    } else if ((r.body as any)?.error === "connect_not_enabled") {
      setConnectDisabled(true);
      setMsg((r.body as any).message);
    } else {
      setMsg(`Falha ao gerar link: ${(r.body as any)?.message ?? r.status}`);
    }
  };

  const refreshAccount = async (acctId: string) => {
    setRowBusy(`${acctId}:sync`);
    await api(`/api/admin/connect/accounts/${acctId}/refresh`, {
      method: "POST",
      body: JSON.stringify({}),
    });
    await refresh();
    setRowBusy(null);
  };

  // KPIs a partir dos repasses (opcionalmente filtrados por afiliado). Vendas =
  // soma do valor das cobrancas; repasse liquido = comissao - revertido.
  const kpisFor = (code: string | null) => {
    const rows = code
      ? transfers.filter((t) => t.affiliateCode === code)
      : transfers;
    let sales = 0;
    let gross = 0;
    let reversed = 0;
    for (const t of rows) {
      if (t.saleAmount != null) sales += t.saleAmount;
      gross += t.amount;
      reversed += t.amountReversed;
    }
    return { count: rows.length, sales, net: gross - reversed, reversed };
  };

  return (
    <>
      {connectDisabled && !noticeDismissed && (
        <div
          style={{
            marginBottom: 16,
            padding: "10px 14px",
            border: "1px solid var(--warn)",
            background: "var(--warn-bg)",
            color: "var(--warn)",
            borderRadius: 6,
            fontSize: 14,
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <div style={{ flex: 1 }}>
            <strong>Stripe Connect não habilitado nesta conta.</strong> As funções
            de afiliado (criar conta, onboarding, repasses) ficam indisponíveis até
            habilitar o Connect no Dashboard da Stripe (uma vez, por conta).{" "}
            <a
              href="https://dashboard.stripe.com/connect"
              target="_blank"
              rel="noreferrer"
            >
              Habilitar Connect
            </a>
          </div>
          <button
            onClick={() => setNoticeDismissed(true)}
            title="Fechar aviso"
            aria-label="Fechar aviso"
            style={{
              background: "none",
              border: "none",
              color: "var(--warn)",
              cursor: "pointer",
              fontSize: 18,
              lineHeight: 1,
              padding: 0,
            }}
          >
            &times;
          </button>
        </div>
      )}
      <div className="inline-form" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Novo afiliado (Connect Express)</h3>
        <p className="hint">
          Cria uma connected account. Depois gere o link de onboarding (KYC de
          teste), sincronize e rode um checkout com split de comissão.
        </p>
        <div className="form-row" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input
            placeholder="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            placeholder="nome (opcional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            placeholder="código (ex: AFF1)"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            style={{ width: 140 }}
          />
          <input
            placeholder="% comissão"
            value={pct}
            onChange={(e) => setPct(e.target.value)}
            style={{ width: 100 }}
          />
          <button
            className="primary-btn"
            onClick={createAccount}
            disabled={busy || connectDisabled}
            title={connectDisabled ? "Habilite o Connect na conta Stripe" : ""}
          >
            {busy ? "Criando..." : "Criar afiliado"}
          </button>
        </div>
        {msg && <p className="hint">{msg}</p>}
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th>Código</th>
            <th>Nome / Email</th>
            <th>Conta Stripe</th>
            <th>Comissão</th>
            <th>Charges</th>
            <th>Payouts</th>
            <th>Status</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {accounts.length === 0 ? (
            <tr>
              <td colSpan={8} className="empty">
                Nenhum afiliado. Crie um acima.
              </td>
            </tr>
          ) : (
            accounts.map((a) => (
              <tr key={a.id}>
                <td>{a.affiliateCode ?? <span className="muted">—</span>}</td>
                <td>
                  {a.name ?? "—"}
                  <br />
                  <span className="muted">{a.email}</span>
                </td>
                <td><code className="muted">{a.stripeAccountId}</code></td>
                <td>{a.commissionPct}%</td>
                <td>{a.chargesEnabled ? "sim" : <span className="muted">não</span>}</td>
                <td>{a.payoutsEnabled ? "sim" : <span className="muted">não</span>}</td>
                <td><Pill status={a.status} /></td>
                <td>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button
                      onClick={() => onboardingLink(a.stripeAccountId)}
                      disabled={rowBusy === `${a.stripeAccountId}:onboarding`}
                    >
                      {rowBusy === `${a.stripeAccountId}:onboarding`
                        ? "..."
                        : "Onboarding"}
                    </button>
                    <button
                      onClick={() => refreshAccount(a.stripeAccountId)}
                      disabled={rowBusy === `${a.stripeAccountId}:sync`}
                    >
                      {rowBusy === `${a.stripeAccountId}:sync`
                        ? "Sincronizando..."
                        : "Sincronizar"}
                    </button>
                    <button
                      onClick={() => setSelectedAffiliate(a.affiliateCode)}
                      disabled={!a.affiliateCode}
                      title={
                        a.affiliateCode ? "" : "Afiliado sem código"
                      }
                    >
                      Repasses
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <h3 style={{ margin: "24px 0 8px" }}>
        Repasses (comissões)
        {selectedAffiliate ? (
          <>
            {" "}— <strong>{selectedAffiliate}</strong>{" "}
            <button
              className="ghost-btn"
              style={{ marginLeft: 8 }}
              onClick={() => setSelectedAffiliate(null)}
            >
              ver todos
            </button>
          </>
        ) : (
          " — geral"
        )}
      </h3>

      {(() => {
        const k = kpisFor(selectedAffiliate);
        return (
          <div className="stats-grid" style={{ marginBottom: 12 }}>
            <Stat label="Repasses" value={k.count} />
            <KpiCard label="Vendas atribuídas" text={brl(k.sales)} />
            <KpiCard label="Comissão líquida" text={brl(k.net)} />
            <KpiCard label="Estornado (clawback)" text={brl(k.reversed)} />
          </div>
        );
      })()}

      <p className="hint" style={{ marginBottom: 12 }}>
        Transferências feitas aos afiliados nas compras. "Revertido" indica
        clawback (a venda foi reembolsada e a comissão estornada).
      </p>
      <table className="data-table">
        <thead>
          <tr>
            <th>Afiliado</th>
            <th>Venda</th>
            <th>Comissão</th>
            <th>Revertido</th>
            <th>Cobrança</th>
            <th>Quando</th>
          </tr>
        </thead>
        <tbody>
          {(() => {
            const rows = selectedAffiliate
              ? transfers.filter((t) => t.affiliateCode === selectedAffiliate)
              : transfers;
            if (rows.length === 0)
              return (
                <tr>
                  <td colSpan={6} className="empty">
                    Nenhum repasse ainda. Compre com um código de afiliado ativo.
                  </td>
                </tr>
              );
            return rows.map((t) => (
              <tr key={t.id}>
                <td>
                  {t.affiliateCode ? (
                    <strong>{t.affiliateCode}</strong>
                  ) : (
                    <span className="muted">—</span>
                  )}
                  {t.affiliateName ? (
                    <>
                      <br />
                      <span className="muted">{t.affiliateName}</span>
                    </>
                  ) : null}
                </td>
                <td>
                  {t.saleAmount != null
                    ? brl(t.saleAmount, t.currency)
                    : <span className="muted">—</span>}
                </td>
                <td>{brl(t.amount, t.currency)}</td>
                <td>
                  {t.reversed ? (
                    <span className="pill pill-failed">total</span>
                  ) : t.amountReversed > 0 ? (
                    <span className="pill pill-pending">
                      {brl(t.amountReversed, t.currency)}
                    </span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>
                  {t.sourceCharge ? (
                    <code className="muted">{t.sourceCharge}</code>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>{formatDate(t.created)}</td>
              </tr>
            ));
          })()}
        </tbody>
      </table>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────── Cupons

function CouponsTab() {
  const [rows, setRows] = useState<CouponRow[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // form
  const [code, setCode] = useState("");
  const [kind, setKind] = useState<"percent" | "amount">("percent");
  const [value, setValue] = useState("");
  const [duration, setDuration] = useState<"once" | "forever" | "repeating">(
    "once"
  );
  const [maxRedemptions, setMaxRedemptions] = useState("");

  const load = useCallback(() => {
    api<{ data: CouponRow[] }>("/api/admin/coupons").then(
      (r) => r.ok && setRows(r.body.data)
    );
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const create = async () => {
    if (!code || !value) {
      setMsg("Informe o código e o valor do desconto.");
      return;
    }
    setBusy(true);
    setMsg(null);
    const body: Record<string, unknown> = { code, duration };
    if (kind === "percent") body.percentOff = Number(value);
    else body.amountOffCents = Math.round(parseFloat(value.replace(",", ".")) * 100);
    if (maxRedemptions) body.maxRedemptions = Number(maxRedemptions);
    const r = await api("/api/admin/coupons", {
      method: "POST",
      body: JSON.stringify(body),
    });
    setBusy(false);
    if (r.ok) {
      setMsg(`Cupom "${code}" criado.`);
      setCode("");
      setValue("");
      setMaxRedemptions("");
      load();
    } else {
      setMsg(`Falha: ${(r.body as any)?.message ?? r.status}`);
    }
  };

  const deactivate = async (id: string) => {
    await api(`/api/admin/coupons/${id}/deactivate`, {
      method: "POST",
      body: JSON.stringify({}),
    });
    load();
  };

  const discountLabel = (c: CouponRow) =>
    c.percentOff != null
      ? `${c.percentOff}%`
      : c.amountOff != null
        ? brl(c.amountOff, c.currency ?? "brl")
        : "—";

  return (
    <>
      <div className="inline-form" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Novo cupom</h3>
        <p className="hint">
          Cria um cupom real na Stripe (Coupon + Promotion Code). O código vale
          em qualquer checkout da Stripe — loja e apps externos.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input
            placeholder="CÓDIGO (ex: PROMO20)"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            style={{ width: 180 }}
          />
          <select value={kind} onChange={(e) => setKind(e.target.value as any)}>
            <option value="percent">% off</option>
            <option value="amount">R$ off</option>
          </select>
          <input
            placeholder={kind === "percent" ? "ex: 20" : "ex: 15.00"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            style={{ width: 110 }}
          />
          <select
            value={duration}
            onChange={(e) => setDuration(e.target.value as any)}
            title="Duração do desconto em assinaturas"
          >
            <option value="once">once (1ª cobrança)</option>
            <option value="forever">forever (todas)</option>
            <option value="repeating">repeating</option>
          </select>
          <input
            placeholder="máx. usos (opc.)"
            value={maxRedemptions}
            onChange={(e) => setMaxRedemptions(e.target.value)}
            style={{ width: 130 }}
          />
          <button className="primary-btn" onClick={create} disabled={busy}>
            {busy ? "Criando..." : "Criar cupom"}
          </button>
        </div>
        {msg && <p className="hint">{msg}</p>}
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th>Código</th>
            <th>Desconto</th>
            <th>Duração</th>
            <th>Usos</th>
            <th>Validade</th>
            <th>Ativo</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={7} className="empty">
                Nenhum cupom. Crie um acima.
              </td>
            </tr>
          ) : (
            rows.map((c) => (
              <tr key={c.promotionCodeId}>
                <td><strong>{c.code}</strong></td>
                <td>{discountLabel(c)}</td>
                <td><code className="muted">{c.duration ?? "—"}</code></td>
                <td>
                  {c.timesRedeemed}
                  {c.maxRedemptions ? ` / ${c.maxRedemptions}` : ""}
                </td>
                <td>
                  {c.expiresAt
                    ? formatDate(new Date(c.expiresAt * 1000).toISOString())
                    : <span className="muted">—</span>}
                </td>
                <td>
                  <Pill status={c.active && c.valid ? "active" : "inactive"} />
                </td>
                <td>
                  {c.active ? (
                    <button
                      className="ghost-btn"
                      onClick={() => deactivate(c.promotionCodeId)}
                    >
                      Desativar
                    </button>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </>
  );
}
